import {
  CardInstance,
  CardPosition,
  EnginePhase,
  MatchState,
  Seat,
  otherSeat,
} from '../types/state';
import { EventType, GameEvent } from '../types/events';
import { CardKind, getCardKind } from '../types/cards';
import {
  CardLibrary,
  findCard,
  getModifiedAtk,
  getModifiedDef,
  getMonsters,
  getSpellTraps,
  handHasNormalSummonMonster,
  soulExchangeTributeUids,
} from './helpers';
import { lingeringTurnsFor } from './lingeringTurns';

// Headless effect system. Effects operate on plain state through EngineApi and
// emit GameEvents; they never touch Phaser. Mirrors the intent of the client's
// EffectSystem but fully serializable and server-safe.

export enum EngineEffectTrigger {
  ACTIVATE = 'ACTIVATE',
  EQUIP = 'EQUIP',
  FIELD = 'FIELD',
  FLIP = 'FLIP',
  CONTINUOUS = 'CONTINUOUS',
}

export interface EngineApi {
  readonly state: MatchState;
  readonly library: CardLibrary;
  readonly controller: Seat;
  readonly source: CardInstance;
  /**
   * The monster that opened the trap-response window being answered right now (the
   * one just summoned, or the attacker), or null outside such a window. Traps whose
   * ruling ties them to a specific moment — Trap Hole answers a summon and nothing
   * else — gate their activation on this instead of scanning the board.
   */
  readonly responseTrigger: CardInstance | null;
  /**
   * Battle position chosen for a Special Summon this activation will perform
   * (Monster Reborn, Polymerization). Attack if the controller did not pick one
   * (AI seats, or a human who skipped the prompt).
   */
  readonly summonPosition: CardPosition;
  /**
   * Position chosen for this specific summoned monster when one activation
   * Special Summons more than one (The Flute of Summoning Dragon). Falls back
   * to `summonPosition` when the controller did not pick one for `uid`.
   */
  summonPositionFor(uid: string): CardPosition;
  emit(event: GameEvent): void;
  /** Destroys the card. Returns false when Fake Trap replaces that destruction. */
  destroyCard(uid: string): boolean;
  /** True when Fake Trap will be destroyed instead of this Trap. */
  sparedByReplacement(uid: string): boolean;
  sendToGraveyard(uid: string): void;
  banishCard(uid: string, faceDown?: boolean): void;
  changeLifePoints(seat: Seat, delta: number): void;
  /**
   * Draw `count` cards. The engine emits one CARD_DRAWN per card; the duel
   * renderer plays those flights one after another (never in parallel).
   */
  drawCards(seat: Seat, count: number): void;
  addStatModifier(uid: string, atk: number, def: number, sourceUid: string): void;
  /** Printed ATK is ignored until this damage calculation ends. */
  setAtkFinal(uid: string, atk: number): void;
  banishFromGraveyard(seat: Seat, count: number, preferStrongest: boolean): void;
  returnToHand(uid: string): void;
  /** Special-summons a monster (found anywhere) to a seat's free monster zone. */
  specialSummon(uid: string, toSeat: Seat, position: CardPosition): void;
  /** Moves a monster to another seat's field until that seat's End Phase (control change). */
  takeControl(uid: string, toSeat: Seat): void;
  /** Forbids a seat from declaring attacks for the given number of its turns. */
  setAttackLock(seat: Seat, turns: number): void;
  /** Remaining turns the source card stays on the field (shown on the sprite). */
  setTurnCounter(turns: number): void;
  /** Flips a face-down monster face-up, triggering its Flip Effect if it has one. */
  flipFaceUp(uid: string): void;
  /**
   * The monster's battle position already changed from `from` to whatever
   * it is now. Position triggers (Crass Clown and the rest) read that.
   */
  noteBattlePosition(uid: string, from: CardPosition): void;
  /** Whether a seat has at least one free monster zone. */
  hasFreeMonsterZone(seat: Seat): boolean;
  /** Attempts a fusion summon for a seat. If fusionUid is given, summons that specific fusion. */
  fusionSummon(seat: Seat, fusionUid?: string): boolean;
  /** Whether a fusion summon is possible (optionally for a specific extra-deck fusion). */
  canFusionSummon(seat: Seat, fusionUid?: string): boolean;
  /** Extra Normal Summon/Set this turn (Ultimate Offering). */
  grantExtraNormalSummon(): void;
  /** Add one Last Will Special Summon for the controller this turn. */
  armLastWill(): void;
  /** Controller takes 0 battle damage for the rest of this turn (Waboku). */
  setBattleDamageImmune(): void;
  /** Controller's monsters cannot be destroyed by battle this turn (Waboku). */
  setBattleDestroyImmune(): void;
  /** Invert ATK/DEF modifiers until End Phase (Reverse Trap). */
  setReverseStatMods(): void;
  /** Mark an opponent's monster as Tribute fodder this turn (Soul Exchange). */
  markSoulExchange(uid: string): void;
  /** Skip this turn's Battle Phase (Soul Exchange). */
  skipBattlePhase(): void;
  /** Discard the seat's whole hand; returns how many cards went to the GY. */
  discardHand(seat: Seat): number;
  /**
   * Look at cards. Private unless `reveal` (The Stern Mystic): then both
   * seats receive the list so the field can flip them.
   */
  peekCards(
    cards: { uid: string; cardId: string }[],
    opts?: { reveal?: boolean },
  ): void;
  /**
   * Big Eye. A human puts the top cards back in the order they choose.
   * The AI leaves the order and only remembers them.
   */
  orderDeckTop(maxCount: number): void;
  /** Shield & Sword. Face-up monsters swap printed ATK and DEF. */
  swapPrintedStats(): void;
  /** True when Lord of D. forbids targeting this monster. */
  isDragonProtected(uid: string): boolean;
  /**
   * True when this monster was already destroyed by the current battle and is
   * only still on the field until the Damage Step ends. It is not a legal
   * target for "1 monster on the field".
   */
  isPendingBattleDestroy(uid: string): boolean;
  /** Negate the Spell or Trap activation this card chained to. */
  negatePreviousLink(): void;
  /** True when this resolution has an earlier chain link to negate. */
  hasPreviousLink(): boolean;
  /** Undo the summon that opened this window and destroy that monster. */
  negateSummon(): void;
  /** This attack deals 0 battle damage to the controller (Kuriboh). */
  zeroThisAttackDamage(): void;
  /**
   * Traps you control are not destroyed by the effect this card chained to
   * (Fake Trap). Other effects in the same chain still destroy them.
   */
  armFakeTrap(): void;
  /** Stat the guardian boost chose, or null when the player did not pick. */
  chosenBoost(): 'atk' | 'def' | null;
  /** What kind of summon opened the current response, if any. */
  summonWindow(): 'normal' | 'flip' | 'special' | null;
  /** True while a chain link is resolving, after its cost was already paid. */
  resolving(): boolean;
}

export interface EngineEffect {
  id: number;
  trigger: EngineEffectTrigger;
  /** whether activating this effect requires player target selection (handled by host). */
  requiresTarget?: boolean;
  /**
   * The selection is a cost paid when the card joins the chain (Horn of Heaven's
   * tribute, Magic Jammer's discard). It is not re-checked when the chain resolves.
   */
  costSelection?: boolean;
  canActivate?(api: EngineApi): boolean;
  /** validates a specific chosen target uid before the effect is committed. */
  canTarget?(api: EngineApi, targetUid: string): boolean;
  /**
   * Validates the selection as a whole, for effects that need several targets at
   * once and whose legality depends on the combination (Two-Pronged Attack needs
   * exactly two of your monsters plus one of the opponent's). When present it
   * replaces the per-uid `canTarget` check during activation.
   */
  canTargetSet?(api: EngineApi, targetUids: string[]): boolean;
  /** Inclusive size of the target window. Defaults to a single target. */
  minTargets?: number;
  maxTargets?: number;
  /**
   * Legal in the Damage Step (ATK/DEF modifiers and damage reducers).
   * Everything else may only answer the Battle Step or a non-battle window.
   * While the attack target is still Set, these wait until that monster is flipped.
   */
  damageStep?: boolean;
  /**
   * Timings where offering this card is meaningful. Legality is `canActivate`;
   * this only keeps the response window from asking when the effect could not
   * matter yet (Waboku outside the Battle Phase). Ignored in `always` mode.
   * `minimal` mode opens a window for a Set card only when this gate exists
   * and currently passes.
   */
  relevantNow?(api: EngineApi): boolean;
  /**
   * Cards this effect would destroy, already chosen when it joins the chain.
   * Fake Trap reads that list off the previous link. Leave it unset when the
   * effect does not destroy, or the victims are not a known group yet.
   */
  destroys?(api: EngineApi, targetUids?: string[]): string[];
  execute(api: EngineApi, targetUids?: string[]): void;
}

class EngineEffectRegistry {
  private readonly effects = new Map<number, EngineEffect[]>();

  register(effect: EngineEffect): void {
    const list = this.effects.get(effect.id) ?? [];
    list.push(effect);
    this.effects.set(effect.id, list);
  }

  get(cardId: number): EngineEffect[] {
    return this.effects.get(cardId) ?? [];
  }

  getByTrigger(cardId: number, trigger: EngineEffectTrigger): EngineEffect[] {
    return this.get(cardId).filter((e) => e.trigger === trigger);
  }

  has(cardId: number, trigger: EngineEffectTrigger): boolean {
    return this.getByTrigger(cardId, trigger).length > 0;
  }
}

export const engineEffects = new EngineEffectRegistry();

function destroyAllMonsters(api: EngineApi, seats: Seat[]): void {
  for (const seat of seats) {
    for (const m of getMonsters(api.state.zones[seat])) {
      api.destroyCard(m.uid);
    }
  }
}

function cardKindOf(api: EngineApi, card: CardInstance): CardKind {
  const data = api.library.get(card.cardId);
  return data ? getCardKind(data) : CardKind.MONSTER;
}

/** Opponent's (relative to a seat) strongest face-up-or-not monster by ATK, or null. */
function strongestMonster(api: EngineApi, seat: Seat): CardInstance | null {
  const monsters = getMonsters(api.state.zones[seat]);
  if (monsters.length === 0) return null;
  return monsters.reduce((best, m) =>
    atkOf(api, m) > atkOf(api, best) ? m : best,
  );
}

/** Every monster on the field, both sides ("target 1 monster on the field"). */
function fieldMonsters(api: EngineApi): CardInstance[] {
  return ([0, 1] as Seat[]).flatMap((s) => getMonsters(api.state.zones[s]));
}

/**
 * Field monsters that can still be chosen. One already destroyed by this
 * battle stays on the field until the Flip Effect finishes, but it is not a
 * legal target (Hane-Hane and Man-Eater Bug cannot pick themselves when the
 * attack destroyed them).
 */
function targetableFieldMonsters(api: EngineApi): CardInstance[] {
  return fieldMonsters(api).filter((m) => !api.isPendingBattleDestroy(m.uid));
}

/**
 * The monster Trap Hole may destroy right now: the summon that opened this
 * window, still face-up on the opponent's field, with 1000 or more ATK.
 * A monster that already left (the chain destroyed it) is not a victim, so a
 * second copy does not pull it back out of the Graveyard.
 */
function trapHoleVictim(api: EngineApi): CardInstance | null {
  const summoned = api.responseTrigger;
  if (!summoned || !summoned.faceUp) return null;
  const found = findCard(api.state, summoned.uid);
  if (!found || found.location.zone !== 'monsters') return null;
  if (found.location.seat === api.controller) return null;
  if (atkOf(api, summoned) < 1000) return null;
  if (isDragonProtected(api, summoned.uid)) return null;
  return summoned;
}

/**
 * Trap Hole answers a Normal Summon (including Tribute) or a Flip Summon.
 * c4206964.lua registers EVENT_SUMMON_SUCCESS and EVENT_FLIP_SUMMON_SUCCESS
 * only. A Special Summon, such as Last Will, does not open it. The kind is
 * checked at activation; resolution still destroys the monster that was legal
 * when the trap was flipped.
 */
function trapHoleSummon(api: EngineApi): CardInstance | null {
  const kind = api.summonWindow();
  if (kind !== 'normal' && kind !== 'flip') return null;
  return trapHoleVictim(api);
}

export const STARTER_IDS = {
  DIAN_KETO: 84257639,
  OOKAZI: 19523799,
  INVIGORATION: 98374133,
  SWORD_DARK: 37120512,
  TRAP_MASTER: 46461247,
  REINFORCEMENTS: 17814387,
  CASTLE_WALLS: 44209392,
  WABOKU: 12607053,
  CARD_DESTRUCTION: 72892473,
  SOUL_EXCHANGE: 68005187,
  LAST_WILL: 85602018,
  ULTIMATE_OFFERING: 80604091,
  LORD_OF_D: 17985575,
  FLUTE: 43973174,
  WALL_OF_ILLUSION: 13945283,
  WORM_BEAST: 6285791,
  PUPPETEER: 54098121,
  REVERSE_TRAP: 77622396,
  STERN_MYSTIC: 87557188,
  SPY: 81820689,
  TELESCOPE: 17092736,
  JUST_DESSERTS: 24068492,
  CHANGE_OF_HEART: 4031928,
  DE_SPELL: 19159413,
} as const;

function lordOfDFaceUp(api: EngineApi): boolean {
  return fieldMonsters(api).some(
    (m) => m.faceUp && Number(m.cardId) === STARTER_IDS.LORD_OF_D,
  );
}

/** Lord of D.: Dragon-Type monsters on the field cannot be targeted. */
export function isDragonProtected(api: EngineApi, uid: string): boolean {
  if (!lordOfDFaceUp(api)) return false;
  const found = findCard(api.state, uid);
  if (!found || found.location.zone !== 'monsters') return false;
  return api.library.get(found.instance.cardId)?.race === 'Dragon';
}

function atkOf(api: EngineApi, m: CardInstance): number {
  return getModifiedAtk(m, api.library, api.state.reverseStatMods);
}

function controlsLordOfD(api: EngineApi, seat: Seat): boolean {
  return getMonsters(api.state.zones[seat]).some(
    (m) => m.faceUp && Number(m.cardId) === STARTER_IDS.LORD_OF_D,
  );
}

/** A seat's face-up monsters, for effects that may only see public information. */
function faceUpMonsters(api: EngineApi, seat: Seat): CardInstance[] {
  return getMonsters(api.state.zones[seat]).filter((m) => m.faceUp);
}

/** Fissure. Opponent's face-up monsters that share the lowest current ATK. */
export const FISSURE = 66788016;

export function fissureMinGroup(api: EngineApi): CardInstance[] {
  const monsters = faceUpMonsters(api, otherSeat(api.controller));
  if (monsters.length === 0) return [];
  let min = Number.POSITIVE_INFINITY;
  for (const monster of monsters) {
    const atk = atkOf(api, monster);
    if (atk < min) min = atk;
  }
  return monsters.filter((monster) => atkOf(api, monster) === min);
}

/**
 * Face-up monsters on both fields. 2002 Expert Rules: face-down monsters
 * have no public ATK/DEF, so stat-boost traps cannot select them.
 */
function faceUpFieldMonsters(api: EngineApi): CardInstance[] {
  return fieldMonsters(api).filter((m) => m.faceUp);
}

/** Monsters in the opponent's graveyard (Gravedigger Ghoul). */
function oppGyMonsters(api: EngineApi): CardInstance[] {
  return api.state.zones[otherSeat(api.controller)].graveyard.filter(
    (c) => cardKindOf(api, c) === CardKind.MONSTER,
  );
}

/** Face-up Trap Cards on either Spell/Trap row (Remove Trap). */
function faceUpTraps(api: EngineApi): CardInstance[] {
  return ([0, 1] as Seat[]).flatMap((s) => spellTrapsOfKind(api, s, CardKind.TRAP, true));
}

/**
 * The monster the controller picked, or — when no choice was made because an AI
 * controls the effect — the opponent's strongest legal monster. If that side is
 * empty the effect is still mandatory, so it falls back to the controller's own
 * field (including the monster that just flipped, when the attack did not
 * destroy it). A monster already destroyed by this battle is never chosen.
 */
function pickFieldMonster(api: EngineApi, targetUids?: string[]): CardInstance | null {
  const legal = targetableFieldMonsters(api);
  const uid = targetUids?.[0];
  if (uid) return legal.find((m) => m.uid === uid) ?? null;
  const onSeat = (seat: Seat) =>
    legal.filter((m) => findCard(api.state, m.uid)?.location.seat === seat);
  const best = (monsters: CardInstance[]) =>
    monsters.length === 0
      ? null
      : monsters.reduce((top, m) => (atkOf(api, m) > atkOf(api, top) ? m : top));
  return best(onSeat(otherSeat(api.controller))) ?? best(onSeat(api.controller));
}

/**
 * Two-Pronged Attack's selection: exactly two monsters the controller owns plus one
 * the opponent owns, all distinct and all still on the field. Returns them in
 * destruction order, or null when the selection is not that legal 2 + 1 split.
 */
function twoProngedTargets(api: EngineApi, uids: string[] | undefined): string[] | null {
  if (!uids || new Set(uids).size !== 3) return null;
  const own: string[] = [];
  const foe: string[] = [];
  for (const uid of uids) {
    const found = findCard(api.state, uid);
    if (found?.location.zone !== 'monsters') return null;
    if (found.location.seat === api.controller) own.push(uid);
    else foe.push(uid);
  }
  if (own.length !== 2 || foe.length !== 1) return null;
  return [...own, ...foe];
}

/**
 * Two-Pronged Attack trade the AI will take: its two weakest monsters for the
 * opponent's strongest face-up monster. Face-down threats are ignored so the
 * heuristic never reads hidden ATK/DEF.
 */
export function twoProngedTradeForAi(
  state: MatchState,
  library: CardLibrary,
  controller: Seat,
): string[] | null {
  const reverse = state.reverseStatMods;
  const own = [...getMonsters(state.zones[controller])].sort(
    (a, b) =>
      getModifiedAtk(a, library, reverse) - getModifiedAtk(b, library, reverse),
  );
  const foeFaceUp = getMonsters(state.zones[otherSeat(controller)]).filter((m) => m.faceUp);
  if (own.length < 2 || foeFaceUp.length === 0) return null;
  const foe = foeFaceUp.reduce((best, m) =>
    getModifiedAtk(m, library, reverse) > getModifiedAtk(best, library, reverse)
      ? m
      : best,
  );
  return [own[0].uid, own[1].uid, foe.uid];
}

/**
 * True when Two-Pronged Attack removes more public ATK than the AI sacrifices.
 */
export function isTwoProngedTradeFavorable(
  state: MatchState,
  library: CardLibrary,
  controller: Seat,
): boolean {
  const pick = twoProngedTradeForAi(state, library, controller);
  if (!pick) return false;
  const own0 = findCard(state, pick[0]);
  const own1 = findCard(state, pick[1]);
  const foe = findCard(state, pick[2]);
  if (!own0 || !own1 || !foe) return false;
  const reverse = state.reverseStatMods;
  const sacrificed =
    getModifiedAtk(own0.instance, library, reverse) +
    getModifiedAtk(own1.instance, library, reverse);
  return getModifiedAtk(foe.instance, library, reverse) > sacrificed;
}

/** AI trade for Two-Pronged Attack: its two weakest for the opponent's strongest. */
function twoProngedAutoPick(api: EngineApi): string[] | null {
  return (
    twoProngedTradeForAi(api.state, api.library, api.controller) ??
    fallbackTwoProngedPick(api)
  );
}

/** Last resort when every opponent monster is face-down (activation still legal). */
function fallbackTwoProngedPick(api: EngineApi): string[] | null {
  const own = [...getMonsters(api.state.zones[api.controller])].sort(
    (a, b) => atkOf(api, a) - atkOf(api, b),
  );
  const foe = strongestMonster(api, otherSeat(api.controller));
  if (own.length < 2 || !foe) return null;
  return [own[0].uid, own[1].uid, foe.uid];
}

/** Spell/Trap cards of a given kind sitting in a seat's spell/trap zones. */
function spellTrapsOfKind(
  api: EngineApi,
  seat: Seat,
  kind: CardKind,
  faceUpOnly = false,
): CardInstance[] {
  return api.state.zones[seat].spellTraps.filter(
    (c): c is CardInstance => c !== null && cardKindOf(api, c) === kind && (!faceUpOnly || c.faceUp),
  );
}

/** Spell/Trap zone cards plus the Field Zone card, if any. */
function backrowAndField(api: EngineApi, seat: Seat): CardInstance[] {
  const z = api.state.zones[seat];
  const out = z.spellTraps.filter((c): c is CardInstance => c !== null);
  if (z.fieldSpell) out.push(z.fieldSpell);
  return out;
}

/** Every Spell, Trap, and Field Spell on the field, except one card. */
function spellTrapsOnField(api: EngineApi, exceptUid?: string): CardInstance[] {
  const out: CardInstance[] = [];
  for (const seat of [0, 1] as Seat[]) {
    for (const c of backrowAndField(api, seat)) {
      if (c.uid !== exceptUid) out.push(c);
    }
  }
  return out;
}

/**
 * The chosen Spell/Trap-zone Trap this effect would destroy.
 * A face-down Spell is not a Trap, so Fake Trap does not answer it.
 */
function targetedTrapUid(api: EngineApi, targetUids?: string[]): string[] {
  const uid = targetUids?.[0];
  if (!uid) return [];
  const found = findCard(api.state, uid);
  if (!found || found.location.zone !== 'spellTraps') return [];
  if (cardKindOf(api, found.instance) !== CardKind.TRAP) return [];
  return [uid];
}

/**
 * Legal targets for a "look at it, then destroy or re-Set" flip effect (Reaper of
 * the Cards, Armed Ninja) and for De-Spell: any face-down Set card — hidden
 * information, so it is targeted blindly and only revealed on resolution — plus
 * face-up cards of the kind the effect actually destroys. Both Spell/Trap rows
 * and the Field Zone are searched. The activating card itself is never legal:
 * a Spell is not on the field as a valid target at the moment it declares
 * activation, and it is already destined for the Graveyard after it resolves.
 * Flip monsters skip themselves for free (they sit in a monster zone).
 */
function revealTargets(api: EngineApi, destroys: CardKind): CardInstance[] {
  const out: CardInstance[] = [];
  const self = api.source.uid;
  for (const seat of [0, 1] as Seat[]) {
    for (const c of backrowAndField(api, seat)) {
      if (c.uid === self) continue;
      if (!c.faceUp || cardKindOf(api, c) === destroys) out.push(c);
    }
  }
  return out;
}

/**
 * Target an AI picks for a reveal effect: something it can actually destroy on the
 * far side first, then anything of the opponent's, and only then its own cards
 * (activation is mandatory, so a self-hit may be the sole legal play).
 */
function autoPickReveal(api: EngineApi, targets: CardInstance[], destroys: CardKind): CardInstance | undefined {
  const oppUids = new Set(backrowAndField(api, otherSeat(api.controller)).map((c) => c.uid));
  return (
    targets.find((c) => oppUids.has(c.uid) && cardKindOf(api, c) === destroys) ??
    targets.find((c) => oppUids.has(c.uid)) ??
    targets.find((c) => cardKindOf(api, c) === destroys) ??
    targets[0]
  );
}

/**
 * Shared resolution for Reaper of the Cards, Trap Master, Armed Ninja, and
 * De-Spell: reveal the chosen card, destroy it if it is the kind this effect
 * destroys, otherwise put it back face-down untouched. A card revealed this
 * way never activates or triggers.
 *
 * The card chosen at activation stays the target. If that Trap chained and
 * already left the field, nothing else is destroyed. autoPickReveal is only
 * for a resolution that never received a choice (the AI).
 */
function resolveReveal(api: EngineApi, targetUids: string[] | undefined, destroys: CardKind): void {
  const targets = revealTargets(api, destroys);
  const uid = targetUids?.[0];
  const chosen = uid
    ? targets.find((c) => c.uid === uid)
    : autoPickReveal(api, targets, destroys);
  if (!chosen) return;

  // A face-down Set is hidden information, so reveal it (the client animates the
  // flip). A face-up target is already public: do not emit CARD_FLIPPED or a
  // "was a Spell/Trap" line, or the renderer plays a flip that never happened.
  const wasFaceDown = !chosen.faceUp;
  const name = api.library.get(chosen.cardId)?.name ?? 'The Set card';
  const kind = cardKindOf(api, chosen);
  const label = kind === CardKind.TRAP ? 'Trap' : 'Spell';
  const kindCode = kind === CardKind.TRAP ? 'trap' : 'spell';
  if (kind === destroys) {
    if (api.sparedByReplacement(chosen.uid)) {
      api.destroyCard(chosen.uid);
      return;
    }
    if (wasFaceDown) {
      api.emit({ type: EventType.CARD_FLIPPED, cardUid: chosen.uid, cardId: chosen.cardId, faceUp: true });
      api.emit({
        type: EventType.MESSAGE,
        text: `${name} was a ${label} — destroyed.`,
        code: 'revealDestroyed',
        vars: { cardId: chosen.cardId, kind: kindCode },
      });
    }
    api.destroyCard(chosen.uid);
  } else if (wasFaceDown) {
    api.emit({ type: EventType.CARD_FLIPPED, cardUid: chosen.uid, cardId: chosen.cardId, faceUp: true });
    api.emit({
      type: EventType.MESSAGE,
      text: `${name} was a ${label} — Set again (not destroyed).`,
      code: 'revealSetAgain',
      vars: { cardId: chosen.cardId, kind: kindCode },
    });
  }
}

/**
 * Gate Guardian and Harpie Lady Sisters can leave the Graveyard only after
 * their own Special Summon procedure. A discard never sets that flag.
 */
function canLeaveGraveyard(card: CardInstance): boolean {
  if (!cannotNormalSummon(card.cardId)) return true;
  return card.properlySummoned === true;
}

/** Strongest monster card sitting in either graveyard, or null. */
function strongestGraveyardMonster(api: EngineApi): CardInstance | null {
  const candidates: CardInstance[] = [];
  for (const seat of [0, 1] as Seat[]) {
    for (const c of api.state.zones[seat].graveyard) {
      if (cardKindOf(api, c) === CardKind.MONSTER && canLeaveGraveyard(c)) candidates.push(c);
    }
  }
  if (candidates.length === 0) return null;
  return candidates.reduce((best, m) =>
    atkOf(api, m) > atkOf(api, best) ? m : best,
  );
}

/** A monster instance with the given uid sitting in either graveyard, or null. */
function graveyardMonster(api: EngineApi, uid: string): CardInstance | null {
  for (const seat of [0, 1] as Seat[]) {
    const c = api.state.zones[seat].graveyard.find((g) => g.uid === uid);
    if (c && cardKindOf(api, c) === CardKind.MONSTER && canLeaveGraveyard(c)) return c;
  }
  return null;
}

function registerBurn(cardId: number, damage: number): void {
  engineEffects.register({
    id: cardId,
    trigger: EngineEffectTrigger.ACTIVATE,
    execute: (api) => {
      api.changeLifePoints(otherSeat(api.controller), -damage);
    },
  });
}

function registerHeal(cardId: number, amount: number): void {
  engineEffects.register({
    id: cardId,
    trigger: EngineEffectTrigger.ACTIVATE,
    execute: (api) => {
      api.changeLifePoints(api.controller, amount);
    },
  });
}

export interface EquipConfig {
  cardId: number;
  name: string;
  atkBoost: number;
  defBoost: number;
  raceFilter?: string;
  /** This race cannot be equipped (Paralyzing Potion: Machine). */
  excludeRace?: string;
  /** Only a monster the player who activates the Spell controls. */
  controllerOnly?: boolean;
  attributeFilter?: string;
  /** Equip that changes ATK/DEF — legal in the Damage Step if it were SS2. */
  damageStep?: boolean;
  /**
   * Extra ATK on each Standby Phase of the equipped monster's controller,
   * while this Spell stays equipped. June 2002 "its Standby Phases".
   */
  standbyAtk?: number;
}

const LOB_EQUIPS: EquipConfig[] = [
  { cardId: 61854111, name: 'Legendary Sword', atkBoost: 300, defBoost: 300, raceFilter: 'Warrior' },
  { cardId: 46009906, name: 'Beast Fangs', atkBoost: 300, defBoost: 300, raceFilter: 'Beast' },
  { cardId: 15052462, name: 'Violet Crystal', atkBoost: 300, defBoost: 300, raceFilter: 'Zombie' },
  { cardId: 91595718, name: 'Book of Secret Arts', atkBoost: 300, defBoost: 300, raceFilter: 'Spellcaster' },
  { cardId: 77027445, name: 'Power of Kaishin', atkBoost: 300, defBoost: 300, raceFilter: 'Aqua' },
  { cardId: 4614116, name: 'Dark Energy', atkBoost: 300, defBoost: 300, raceFilter: 'Fiend' },
  { cardId: 77007920, name: 'Laser Cannon Armor', atkBoost: 300, defBoost: 300, raceFilter: 'Insect' },
  { cardId: 1435851, name: 'Dragon Treasure', atkBoost: 300, defBoost: 300, raceFilter: 'Dragon' },
  { cardId: 37820550, name: 'Electro-Whip', atkBoost: 300, defBoost: 300, raceFilter: 'Thunder' },
  { cardId: 98252586, name: 'Follow Wind', atkBoost: 300, defBoost: 300, raceFilter: 'Winged Beast' },
  { cardId: 25769732, name: 'Machine Conversion Factory', atkBoost: 300, defBoost: 300, raceFilter: 'Machine' },
  { cardId: 36607978, name: 'Mystical Moon', atkBoost: 300, defBoost: 300, raceFilter: 'Beast-Warrior' },
  { cardId: 1557499, name: 'Silver Bow and Arrow', atkBoost: 300, defBoost: 300, raceFilter: 'Fairy' },
  { cardId: 39774685, name: 'Vile Germs', atkBoost: 300, defBoost: 300, raceFilter: 'Plant' },
  { cardId: 51267887, name: 'Raise Body Heat', atkBoost: 300, defBoost: 300, raceFilter: 'Dinosaur' },
  { cardId: 98374133, name: 'Invigoration', atkBoost: 400, defBoost: -200, attributeFilter: 'EARTH', damageStep: true },
  { cardId: 37120512, name: 'Sword of Dark Destruction', atkBoost: 400, defBoost: -200, attributeFilter: 'DARK' },
  // Metal Raiders. Any face-up monster. The return to the top of the Deck
  // is a Graveyard trigger in GameEngine, not this boost.
  { cardId: 98495314, name: 'Sword of Deep-Seated', atkBoost: 500, defBoost: 500 },
  // A non-Machine cannot attack while this is equipped. Either side.
  { cardId: 50152549, name: 'Paralyzing Potion', atkBoost: 0, defBoost: 0, excludeRace: 'Machine' },
  // Your monster only. −500 ATK and DEF. The attack rule lives in GameEngine.
  { cardId: 20436034, name: 'Ring of Magnetism', atkBoost: -500, defBoost: -500, controllerOnly: true },
  // June 2002. Non-Machine. −300 ATK on each of that monster's Standby Phases.
  {
    cardId: 24668830,
    name: 'Germ Infection',
    atkBoost: 0,
    defBoost: 0,
    excludeRace: 'Machine',
    standbyAtk: -300,
  },
  // June 2002. +700 ATK now, then −200 ATK on each of that monster's Standby Phases.
  { cardId: 83225447, name: 'Stim-Pack', atkBoost: 700, defBoost: 0, standbyAtk: -200 },
];

const EQUIP_BY_ID = new Map<number, EquipConfig>(LOB_EQUIPS.map((e) => [e.cardId, e]));

/** Equip boost config by card id, if the card is a known equip spell. */
export function getEquipConfig(cardId: number): EquipConfig | undefined {
  return EQUIP_BY_ID.get(cardId);
}

function canEquipTo(api: EngineApi, config: EquipConfig, monster: CardInstance): boolean {
  if (!monster.faceUp) return false;
  if (isDragonProtected(api, monster.uid)) return false;
  const data = api.library.get(monster.cardId);
  if (!data) return false;
  if (config.raceFilter && data.race !== config.raceFilter) return false;
  if (config.excludeRace && data.race === config.excludeRace) return false;
  if (config.attributeFilter && data.attribute?.toUpperCase() !== config.attributeFilter) return false;
  if (config.controllerOnly) {
    const found = findCard(api.state, monster.uid);
    if (!found || found.location.seat !== api.controller) return false;
  }
  return true;
}

/** Face-up monsters on either field that this equip may legally attach to. */
function legalEquipHosts(api: EngineApi, config: EquipConfig): CardInstance[] {
  return fieldMonsters(api).filter((m) => canEquipTo(api, config, m));
}

/** Strongest legal host by current ATK — what the AI attaches to. */
function autoPickEquipHost(api: EngineApi, config: EquipConfig): CardInstance | undefined {
  return legalEquipHosts(api, config)
    .slice()
    .sort((a, b) => atkOf(api, b) - atkOf(api, a))[0];
}

/** Face-up monster (on either field) that a chosen target uid refers to, or null. */
function findFieldMonster(api: EngineApi, uid: string): CardInstance | null {
  const found = findCard(api.state, uid);
  if (!found || found.location.zone !== 'monsters') return null;
  return found.instance;
}

export interface FieldSpellConfig {
  cardId: number;
  name: string;
  atkBoost: number;
  defBoost: number;
  boostedRaces: string[];
  /** Races the terrain works against, losing the same amount (Umi, Yami). */
  penalizedRaces?: string[];
}

const LOB_FIELD_SPELLS: FieldSpellConfig[] = [
  { cardId: 87430998, name: 'Forest', atkBoost: 200, defBoost: 200, boostedRaces: ['Beast', 'Insect', 'Beast-Warrior', 'Plant'] },
  { cardId: 23424603, name: 'Wasteland', atkBoost: 200, defBoost: 200, boostedRaces: ['Dinosaur', 'Zombie', 'Rock'] },
  { cardId: 50913601, name: 'Mountain', atkBoost: 200, defBoost: 200, boostedRaces: ['Dragon', 'Winged Beast', 'Thunder'] },
  { cardId: 86318356, name: 'Sogen', atkBoost: 200, defBoost: 200, boostedRaces: ['Warrior', 'Beast-Warrior'] },
  {
    cardId: 22702055,
    name: 'Umi',
    atkBoost: 200,
    defBoost: 200,
    boostedRaces: ['Fish', 'Sea Serpent', 'Thunder', 'Aqua'],
    penalizedRaces: ['Machine', 'Pyro'],
  },
  {
    cardId: 59197169,
    name: 'Yami',
    atkBoost: 200,
    defBoost: 200,
    boostedRaces: ['Fiend', 'Spellcaster'],
    penalizedRaces: ['Fairy'],
  },
];

const FIELD_SPELL_BY_ID = new Map<number, FieldSpellConfig>(LOB_FIELD_SPELLS.map((f) => [f.cardId, f]));

/** Field spell boost config by card id, if the card is a known field spell. */
export function getFieldSpellConfig(cardId: number): FieldSpellConfig | undefined {
  return FIELD_SPELL_BY_ID.get(cardId);
}

/**
 * June 2002 Metal Raiders attribute auras. While the monster is face-up,
 * every face-up monster of `boost` gains 500 ATK and every face-up monster
 * of `penalize` loses 400 ATK. DEF is unchanged. Each copy applies.
 */
export interface AttributeAura {
  cardId: number;
  name: string;
  boost: string;
  penalize: string;
}

export const AURA_BOOST_ATK = 500;
export const AURA_PENALTY_ATK = 400;

const ATTRIBUTE_AURAS: AttributeAura[] = [
  { cardId: 67629977, name: 'Hoshiningen', boost: 'LIGHT', penalize: 'DARK' },
  { cardId: 80741828, name: "Witch's Apprentice", boost: 'DARK', penalize: 'LIGHT' },
  { cardId: 8201910, name: 'Star Boy', boost: 'WATER', penalize: 'FIRE' },
  { cardId: 68658728, name: 'Little Chimera', boost: 'FIRE', penalize: 'WATER' },
  { cardId: 7489323, name: 'Milus Radiant', boost: 'EARTH', penalize: 'WIND' },
  { cardId: 28470714, name: 'Bladefly', boost: 'WIND', penalize: 'EARTH' },
];

const AURA_BY_ID = new Map<number, AttributeAura>(ATTRIBUTE_AURAS.map((a) => [a.cardId, a]));

/** The attribute pair this face-up monster boosts, if it is one of the six auras. */
export function attributeAuraOf(cardId: number): AttributeAura | undefined {
  return AURA_BY_ID.get(cardId);
}

/** ATK delta this aura gives a monster of `attribute`. 0 when the attribute is neither side. */
export function attributeAuraDelta(aura: AttributeAura, attribute: string | undefined): number {
  const attr = (attribute ?? '').toUpperCase();
  if (attr === aura.boost) return AURA_BOOST_ATK;
  if (attr === aura.penalize) return -AURA_PENALTY_ATK;
  return 0;
}

/**
 * June 2002 count auras. Each applies only while that monster is face-up,
 * and the number is read again whenever the board, hand, or Graveyard changes.
 *
 * Muka Muka (`c46657337.lua`): +300 ATK and DEF per card in its controller's hand.
 * Shadow Ghoul (printed text + `c30778711.lua`): +100 ATK per monster in its
 * controller's Graveyard. Spells and Traps do not count.
 * Swamp Battleguard (`c40453765.lua`) and Lava Battleguard (MRD-115 original:
 * +500 ATK for each face-up partner on your side of the field): each face-up
 * copy of the other on your side adds 500 ATK. A Set partner does not count.
 */
export const MUKA_MUKA = 46657337;
export const SHADOW_GHOUL = 30778711;
export const SWAMP_BATTLEGUARD = 40453765;
export const LAVA_BATTLEGUARD = 20394040;

export const MUKA_PER_CARD = 300;
export const GHOUL_PER_MONSTER = 100;
export const BATTLEGUARD_PER_PARTNER = 500;

/**
 * June 2002. Insect Soldiers of the Sky (`c7019529.lua`): +1000 ATK during the
 * Damage Step only, and only when it is the attacker and the defender is WIND.
 * The defender is already face-up by then, including a monster that was Set.
 * Castle of Dark Illusions (MRD-073): FLIP gives every Zombie on the field
 * +200 ATK and DEF. While Castle stays face-up, each of your next 4 Standby
 * Phases does that again to the Zombies that are on the field at that moment.
 * Those points stay on the Zombie after Castle leaves. A Zombie that arrives
 * later does not receive the earlier pulses.
 * Pumpking the King of Ghosts (MRD-079): while a face-up Castle is anywhere
 * on the field, Pumpking has +100 ATK and DEF. Each of your Standby Phases
 * while that is true adds another 100, four times. Those Standby points stay
 * if Castle leaves; the flat 100 does not. The count does not reset.
 */
export const INSECT_SOLDIERS = 7019529;
export const CASTLE_OF_DARK_ILLUSIONS = 62121;
export const PUMPKING = 29155212;
export const INSECT_SOLDIERS_BOOST = 1000;
export const CASTLE_PULSE = 200;
export const CASTLE_STANDBYS = 4;
export const PUMPKING_FLAT = 100;
export const PUMPKING_STANDBYS = 4;

/**
 * June 2002 attack locks. Dark Elf (`c21417692.lua`) pays 1000 Life Points
 * to declare an attack. Paralyzing Potion (`c50152549.lua`) stops a
 * non-Machine. Ring of Magnetism (`c20436034.lua`) is −500 ATK and DEF on
 * your own monster, and the opponent can only attack that monster. Electric
 * Lizard (`c55875323.lua`) stops a non-Zombie attacker on its next turn.
 * Steel Scorpion's original printing destroys a non-Machine attacker at the
 * End Phase of the opponent's 3rd turn, counting the attack turn. The later
 * English "2nd turn" erratum is not used. The Unhappy Maiden
 * (`c51275027.lua`) ends the Battle Phase when battle sends her to the GY.
 * The turn continues in Main Phase 2.
 */
export const DARK_ELF = 21417692;
export const DARK_ELF_COST = 1000;
export const PARALYZING_POTION = 50152549;
export const RING_OF_MAGNETISM = 20436034;
export const ELECTRIC_LIZARD = 55875323;
export const STEEL_SCORPION = 13599884;
export const STEEL_SCORPION_ENDS = 3;
export const UNHAPPY_MAIDEN = 51275027;

/**
 * June 2002 position triggers. Crass Clown (`c93889755.lua`) returns 1
 * opposing monster to the hand when it goes from Defense to Attack,
 * including a Flip Summon. Dream Clown (`c13215230.lua`) destroys 1
 * opposing monster when it goes from Attack to face-up Defense. Tainted
 * Wisdom (`c28725004.lua`) shuffles its controller's Deck on that same
 * Attack-to-Defense change. Yado Karu (`c29380133.lua`) does too: any
 * number of cards from the hand go to the bottom of the Deck, in the
 * chosen order, with no draw. The text on the card in this game is that
 * Attack-to-Defense change.
 */
export const CRASS_CLOWN = 93889755;
export const DREAM_CLOWN = 13215230;
export const TAINTED_WISDOM = 28725004;
export const YADO_KARU = 29380133;

export type PositionTrigger = 'crass' | 'dream' | 'tainted' | 'yado';

export function positionTriggerOf(
  cardId: string,
  from: CardPosition,
  to: CardPosition,
): PositionTrigger | null {
  const id = Number(cardId);
  if (from === to) return null;
  if (id === CRASS_CLOWN && from === CardPosition.DEFENSE && to === CardPosition.ATTACK) {
    return 'crass';
  }
  if (id === DREAM_CLOWN && from === CardPosition.ATTACK && to === CardPosition.DEFENSE) {
    return 'dream';
  }
  if (id === TAINTED_WISDOM && from === CardPosition.ATTACK && to === CardPosition.DEFENSE) {
    return 'tainted';
  }
  if (id === YADO_KARU && from === CardPosition.ATTACK && to === CardPosition.DEFENSE) {
    return 'yado';
  }
  return null;
}

export type AttackLockCode = 'paralyzingPotion' | 'electricLizard' | 'darkElfCost';

function monsterHasFaceUpEquip(state: MatchState, monster: CardInstance, cardId: number): boolean {
  for (const uid of monster.equippedUids) {
    const found = findCard(state, uid);
    if (found?.instance.faceUp && Number(found.instance.cardId) === cardId) return true;
  }
  return false;
}

/** Why this face-up attacker cannot declare an attack, if it cannot. */
export function attackLockCode(
  state: MatchState,
  seat: Seat,
  monster: CardInstance,
): AttackLockCode | null {
  if (monsterHasFaceUpEquip(state, monster, PARALYZING_POTION)) return 'paralyzingPotion';
  if (monster.lizardLockTurn !== undefined && monster.lizardLockTurn === state.absoluteTurnCount) {
    return 'electricLizard';
  }
  if (Number(monster.cardId) === DARK_ELF && state.zones[seat].lifePoints < DARK_ELF_COST) {
    return 'darkElfCost';
  }
  return null;
}

/**
 * Monsters on `defenderSeat` that the opponent must attack, if they attack.
 * Empty when Ring of Magnetism is not forcing a target.
 */
export function magnetismTargets(state: MatchState, defenderSeat: Seat): CardInstance[] {
  return getMonsters(state.zones[defenderSeat]).filter((m) =>
    monsterHasFaceUpEquip(state, m, RING_OF_MAGNETISM),
  );
}

/** Zombies in either monster zone. Face-down copies count; their type is real. */
export function fieldZombies(state: MatchState, library: CardLibrary): CardInstance[] {
  const out: CardInstance[] = [];
  for (const seat of [0, 1] as Seat[]) {
    for (const m of getMonsters(state.zones[seat])) {
      if (library.get(m.cardId)?.race === 'Zombie') out.push(m);
    }
  }
  return out;
}

function registerFieldSpell(config: FieldSpellConfig): void {
  // Field spells apply continuously; the engine recomputes their boosts whenever
  // the board changes (see GameEngine.recomputeFieldSpells). The effect itself is a
  // marker so activation is permitted and the card stays on the field.
  engineEffects.register({
    id: config.cardId,
    trigger: EngineEffectTrigger.FIELD,
    execute: () => {
      /* boosts handled centrally by the engine */
    },
  });
}

function registerEquip(config: EquipConfig): void {
  engineEffects.register({
    id: config.cardId,
    trigger: EngineEffectTrigger.EQUIP,
    requiresTarget: true,
    ...(config.damageStep ? { damageStep: true } : {}),
    canActivate: (api) => legalEquipHosts(api, config).length > 0,
    canTarget: (api, uid) => {
      const m = findFieldMonster(api, uid);
      return !!m && canEquipTo(api, config, m);
    },
    execute: (api, targetUids) => {
      const uid = targetUids?.[0];
      const picked = uid ? findFieldMonster(api, uid) : null;
      const target =
        picked && canEquipTo(api, config, picked) ? picked : uid ? null : autoPickEquipHost(api, config);
      if (!target) return;
      if (config.atkBoost !== 0 || config.defBoost !== 0) {
        api.addStatModifier(target.uid, config.atkBoost, config.defBoost, api.source.uid);
      }
      target.equippedUids.push(api.source.uid);
      api.source.equippedToUid = target.uid;
    },
  });
}

/** Battle traps stay quiet outside the Battle Phase. */
function relevantInBattle(api: EngineApi): boolean {
  return api.state.phase === EnginePhase.BATTLE;
}

/**
 * Also true when the previous chain link would destroy this copy.
 * Chaining then is the only way the effect resolves before the card leaves.
 */
function relevantInBattleOrDoomed(api: EngineApi): boolean {
  return relevantInBattle(api) || lastLinkWouldDestroyThis(api);
}

const DRAGON_CAPTURE_JAR = 50045299;

/** A face-up Jar already applies the lock. A second copy does not stack. */
function jarAlreadyFaceUp(api: EngineApi): boolean {
  return ([0, 1] as Seat[]).some((seat) =>
    getSpellTraps(api.state.zones[seat]).some(
      (c) => c.faceUp && Number(c.cardId) === DRAGON_CAPTURE_JAR,
    ),
  );
}

/**
 * Auto mode should ask only when flipping would lock an opponent's Dragon.
 * Face-down monsters stay hidden: prompting on a Set Dragon would reveal it.
 * The controller's own Dragons do not open a window; flipping then would
 * lock the monster they are about to attack with.
 */
function jarWorthFlipping(api: EngineApi): boolean {
  if (jarAlreadyFaceUp(api)) return false;
  return getMonsters(api.state.zones[otherSeat(api.controller)]).some(
    (m) => m.faceUp && api.library.get(m.cardId)?.race === 'Dragon',
  );
}

let registered = false;

/** Registers the built-in headless effects. Idempotent. */
export function registerEngineEffects(): void {
  if (registered) return;
  registered = true;

  // Dark Hole (53129443): destroy all monsters on the field (yours count).
  engineEffects.register({
    id: 53129443,
    trigger: EngineEffectTrigger.ACTIVATE,
    canActivate: (api) => fieldMonsters(api).length > 0,
    execute: (api) => destroyAllMonsters(api, [0, 1]),
  });

  // Raigeki (12580477): destroy all opponent monsters.
  engineEffects.register({
    id: 12580477,
    trigger: EngineEffectTrigger.ACTIVATE,
    canActivate: (api) => getMonsters(api.state.zones[otherSeat(api.controller)]).length > 0,
    execute: (api) => destroyAllMonsters(api, [otherSeat(api.controller)]),
  });

  // Pot of Greed (55144522): draw 2 cards. Illegal only when the deck is empty
  // (a 1-card deck is legal: you draw it and lose by deck-out). The 2 draws are
  // two CARD_DRAWN events; NetworkDuelScene plays them one at a time.
  engineEffects.register({
    id: 55144522,
    trigger: EngineEffectTrigger.ACTIVATE,
    canActivate: (api) => api.state.zones[api.controller].deck.length > 0,
    execute: (api) => api.drawCards(api.controller, 2),
  });

  // Fissure (66788016): destroy the opponent's lowest-ATK face-up monster.
  // Set monsters are never read. c66788016.lua destroys a unique minimum
  // immediately. When two or more share that ATK, the controller chooses at
  // resolution; GameEngine opens that window before this runs. This execute
  // is the automatic path: one monster, or the AI, which keeps the leftmost
  // monster of the tie.
  engineEffects.register({
    id: FISSURE,
    trigger: EngineEffectTrigger.ACTIVATE,
    canActivate: (api) => faceUpMonsters(api, otherSeat(api.controller)).length > 0,
    execute: (api) => {
      const target = fissureMinGroup(api)[0];
      if (target) api.destroyCard(target.uid);
    },
  });

  // Gravedigger Ghoul (82542267): banish 1 or 2 monsters from the opponent's GY.
  engineEffects.register({
    id: 82542267,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    minTargets: 1,
    maxTargets: 2,
    canActivate: (api) => oppGyMonsters(api).length > 0,
    canTarget: (api, uid) => oppGyMonsters(api).some((c) => c.uid === uid),
    canTargetSet: (api, uids) => {
      if (uids.length < 1 || uids.length > 2) return false;
      const legal = new Set(oppGyMonsters(api).map((c) => c.uid));
      return new Set(uids).size === uids.length && uids.every((u) => legal.has(u));
    },
    execute: (api, targetUids) => {
      const legal = oppGyMonsters(api);
      const chosen = (targetUids ?? []).filter((uid) => legal.some((c) => c.uid === uid));
      const toBanish =
        chosen.length > 0
          ? chosen.slice(0, 2)
          : [...legal]
              .sort((a, b) => atkOf(api, b) - atkOf(api, a))
              .slice(0, 2)
              .map((c) => c.uid);
      for (const uid of toBanish) api.banishCard(uid);
    },
  });

  // Stop Defense (63102017): switch an opponent's Defense-Position monster to
  // face-up Attack Position (works on face-down monsters too).
  engineEffects.register({
    id: 63102017,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    canActivate: (api) =>
      getMonsters(api.state.zones[otherSeat(api.controller)]).some((m) => m.position === CardPosition.DEFENSE),
    canTarget: (api, uid) => {
      const found = findCard(api.state, uid);
      return (
        !!found &&
        found.location.zone === 'monsters' &&
        found.location.seat === otherSeat(api.controller) &&
        found.instance.position === CardPosition.DEFENSE &&
        !isDragonProtected(api, uid)
      );
    },
    execute: (api, targetUids) => {
      const uid = targetUids?.[0];
      if (!uid) return;
      const found = findCard(api.state, uid);
      if (!found || found.location.zone !== 'monsters') return;
      const m = found.instance;
      const from = m.position;
      m.position = CardPosition.ATTACK;
      m.positionChangedThisTurn = true;
      api.emit({ type: EventType.POSITION_CHANGED, cardUid: m.uid, position: m.position });
      // Turning it face-up meets the Flip condition, so a face-down target's Flip
      // Effect triggers and its own controller resolves it (flipFaceUp handles that).
      api.flipFaceUp(m.uid);
      api.noteBattlePosition(m.uid, from);
    },
  });

  // Castle of Dark Illusions: the FLIP pulse and the 4-Standby clock. Later
  // Standby pulses are applied by GameEngine when that phase begins.
  engineEffects.register({
    id: CASTLE_OF_DARK_ILLUSIONS,
    trigger: EngineEffectTrigger.FLIP,
    execute: (api) => {
      let n = 0;
      for (const zombie of fieldZombies(api.state, api.library)) {
        n += 1;
        const source = `castle:${api.source.uid}:flip:${n}`;
        if (zombie.faceUp) api.addStatModifier(zombie.uid, CASTLE_PULSE, CASTLE_PULSE, source);
        else zombie.statModifiers.push({ atk: CASTLE_PULSE, def: CASTLE_PULSE, source });
      }
      const found = findCard(api.state, api.source.uid);
      if (found?.location.zone === 'monsters' && api.source.faceUp) {
        api.setTurnCounter(CASTLE_STANDBYS);
      }
    },
  });

  // --- FLIP effects: resolve when the monster is flipped face-up, whether by battle,
  // a Flip Summon or a card effect (Stop Defense, Swords of Revealing Light). The ones
  // that need a target let a human controller pick it through a selection window; AI
  // controllers auto-pick so their resolution stays synchronous.

  // Man-Eater Bug (54652250): FLIP — target 1 monster on the field; destroy it. The
  // controller picks it (any side, per the card text), so the host opens a selection
  // window for human players; the AI auto-picks. A copy destroyed by the attack
  // that flipped it cannot target itself.
  engineEffects.register({
    id: 54652250,
    trigger: EngineEffectTrigger.FLIP,
    requiresTarget: true,
    canActivate: (api) => targetableFieldMonsters(api).length > 0,
    canTarget: (api, uid) =>
      targetableFieldMonsters(api).some((m) => m.uid === uid) && !isDragonProtected(api, uid),
    execute: (api, targetUids) => {
      const target = pickFieldMonster(api, targetUids);
      if (target) api.destroyCard(target.uid);
    },
  });

  // Hane-Hane (7089711): FLIP — target 1 monster on the field; return it to the hand.
  // It can return itself when it survives the attack or is flipped outside of
  // battle. If that attack destroyed it, it is not a legal target.
  engineEffects.register({
    id: 7089711,
    trigger: EngineEffectTrigger.FLIP,
    requiresTarget: true,
    canActivate: (api) => targetableFieldMonsters(api).length > 0,
    canTarget: (api, uid) =>
      targetableFieldMonsters(api).some((m) => m.uid === uid) && !isDragonProtected(api, uid),
    execute: (api, targetUids) => {
      const target = pickFieldMonster(api, targetUids);
      if (target) api.returnToHand(target.uid);
    },
  });

  // Armed Ninja (9076207): FLIP — target 1 face-up Spell Card, or 1 Set (face-down)
  // Spell/Trap Card, anywhere on the field. On resolution the target is looked at:
  // destroyed if it is a Spell, or put back face-down without being destroyed (and
  // without activating) if it is a Trap. Activation is mandatory whenever a legal
  // target exists, so with only its controller's Set card around it must target that.
  engineEffects.register({
    id: 9076207,
    trigger: EngineEffectTrigger.FLIP,
    requiresTarget: true,
    canActivate: (api) => revealTargets(api, CardKind.SPELL).length > 0,
    canTarget: (api, uid) => revealTargets(api, CardKind.SPELL).some((c) => c.uid === uid),
    execute: (api, targetUids) => resolveReveal(api, targetUids, CardKind.SPELL),
  });

  // Reaper of the Cards (33066139): FLIP — the mirror of Armed Ninja. Target 1
  // face-up Trap Card, or 1 Set (face-down) Spell/Trap Card, anywhere on the field;
  // on resolution destroy it if it is a Trap, or Set it again (no destruction) if it
  // is a Spell. It activates even with no valid target, but then nothing happens
  // (see runFlipEffect). The controller picks blindly; the AI auto-picks.
  engineEffects.register({
    id: 33066139,
    trigger: EngineEffectTrigger.FLIP,
    requiresTarget: true,
    canActivate: (api) => revealTargets(api, CardKind.TRAP).length > 0,
    canTarget: (api, uid) => revealTargets(api, CardKind.TRAP).some((c) => c.uid === uid),
    destroys: (api, targetUids) => targetedTrapUid(api, targetUids),
    execute: (api, targetUids) => resolveReveal(api, targetUids, CardKind.TRAP),
  });

  // Remove Trap (51482758): destroy 1 face-up Trap on either field (your own
  // Dragon Capture Jar is a legal target).
  engineEffects.register({
    id: 51482758,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    canActivate: (api) => faceUpTraps(api).length > 0,
    canTarget: (api, uid) => faceUpTraps(api).some((c) => c.uid === uid),
    destroys: (api, targetUids) => targetedTrapUid(api, targetUids),
    execute: (api, targetUids) => {
      const uid = targetUids?.[0];
      const traps = faceUpTraps(api);
      const picked = uid ? traps.find((c) => c.uid === uid) : undefined;
      const target =
        picked ??
        traps.find((c) => {
          const found = findCard(api.state, c.uid);
          return found?.location.seat === otherSeat(api.controller);
        }) ??
        traps[0];
      if (target) api.destroyCard(target.uid);
    },
  });

  // De-Spell (19159413): destroy 1 Spell on the field. A Set card is looked at:
  // destroyed if it is a Spell, otherwise Set again. Includes the Field Zone.
  // It cannot target itself — without another face-up Spell or a different Set
  // card on the field it is illegal to activate. Targeting another of your own
  // Set cards is still legal.
  engineEffects.register({
    id: 19159413,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    canActivate: (api) => revealTargets(api, CardKind.SPELL).length > 0,
    canTarget: (api, uid) => revealTargets(api, CardKind.SPELL).some((c) => c.uid === uid),
    execute: (api, targetUids) => resolveReveal(api, targetUids, CardKind.SPELL),
  });

  // Two-Pronged Attack (83887306): Trap — destroy 2 monsters you control and 1 the
  // opponent controls. All three are selected by the player (destroying your own two
  // is part of the effect, not a cost), so the choice is never made for them; an AI
  // controller falls back to trading its two weakest for the opponent's strongest.
  engineEffects.register({
    id: 83887306,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    minTargets: 3,
    maxTargets: 3,
    canActivate: (api) =>
      getMonsters(api.state.zones[api.controller]).length >= 2 &&
      getMonsters(api.state.zones[otherSeat(api.controller)]).length > 0,
    canTarget: (api, uid) =>
      findFieldMonster(api, uid) !== null && !isDragonProtected(api, uid),
    canTargetSet: (api, uids) => {
      const split = twoProngedTargets(api, uids);
      return !!split && split.every((u) => !isDragonProtected(api, u));
    },
    execute: (api, targetUids) => {
      const picked = twoProngedTargets(api, targetUids) ?? twoProngedAutoPick(api);
      if (!picked) return;
      for (const uid of picked) api.destroyCard(uid);
    },
  });

  // Trap Hole (4206964): Trap — when the opponent Normal, Tribute, or Flip
  // Summons one monster with 1000 or more ATK, destroy that monster. A Special
  // Summon (Last Will, Monster Reborn, Fusion) does not qualify. 2002 text
  // names the summon, so the player does not pick a target afterwards. Gated on
  // the summon-response window, which keeps it from becoming free removal, and
  // on the summon being the opponent's. `relevantNow` matches that gate so
  // minimal mode still stops for it, and does not stop for a smaller summon
  // or for a Special Summon.
  engineEffects.register({
    id: 4206964,
    trigger: EngineEffectTrigger.ACTIVATE,
    canActivate: (api) => trapHoleSummon(api) !== null,
    relevantNow: (api) => trapHoleSummon(api) !== null,
    execute: (api) => {
      const summoned = trapHoleVictim(api);
      if (summoned) api.destroyCard(summoned.uid);
    },
  });

  // Monster Reborn (83764718): special-summon a chosen monster from either
  // graveyard to the controller's field. The player picks face-up ATK or DEF
  // (api.summonPosition); the AI (no target / no position) uses Attack and the
  // strongest available monster.
  engineEffects.register({
    id: 83764718,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    canActivate: (api) => strongestGraveyardMonster(api) !== null && api.hasFreeMonsterZone(api.controller),
    canTarget: (api, uid) => graveyardMonster(api, uid) !== null,
    execute: (api, targetUids) => {
      const uid = targetUids?.[0];
      const target = (uid ? graveyardMonster(api, uid) : null) ?? strongestGraveyardMonster(api);
      if (target) api.specialSummon(target.uid, api.controller, api.summonPosition);
    },
  });

  // Change of Heart (4031928): take control of a chosen opponent monster until the
  // end of the turn. The AI falls back to the opponent's strongest monster.
  engineEffects.register({
    id: 4031928,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    canActivate: (api) =>
      getMonsters(api.state.zones[otherSeat(api.controller)]).some(
        (m) => !isDragonProtected(api, m.uid),
      ) && api.hasFreeMonsterZone(api.controller),
    canTarget: (api, uid) => {
      const found = findCard(api.state, uid);
      return (
        !!found &&
        found.location.zone === 'monsters' &&
        found.location.seat === otherSeat(api.controller) &&
        !isDragonProtected(api, uid)
      );
    },
    execute: (api, targetUids) => {
      const uid = targetUids?.[0];
      let target: CardInstance | null = null;
      if (uid) {
        const found = findCard(api.state, uid);
        if (
          found &&
          found.location.zone === 'monsters' &&
          found.location.seat === otherSeat(api.controller) &&
          !isDragonProtected(api, uid)
        ) {
          target = found.instance;
        }
      }
      if (!target) {
        const pool = getMonsters(api.state.zones[otherSeat(api.controller)]).filter(
          (m) => !isDragonProtected(api, m.uid),
        );
        target = pool.length === 0 ? null : pool.reduce((best, m) =>
          atkOf(api, m) > atkOf(api, best) ? m : best,
        );
      }
      if (target) api.takeControl(target.uid, api.controller);
    },
  });

  // Swords of Revealing Light (72302403): flip the opponent's face-down monsters
  // face-up and prevent that player from attacking for 3 of their turns. Registered
  // as CONTINUOUS so the card stays on the field while active; the engine removes it
  // to the graveyard once the attack lock expires.
  engineEffects.register({
    id: 72302403,
    trigger: EngineEffectTrigger.CONTINUOUS,
    execute: (api) => {
      const opp = otherSeat(api.controller);
      for (const m of getMonsters(api.state.zones[opp])) {
        if (!m.faceUp) api.flipFaceUp(m.uid);
      }
      const turns = lingeringTurnsFor(api.source.cardId) ?? 3;
      api.setAttackLock(opp, turns);
      api.setTurnCounter(turns);
    },
  });

  // Polymerization (24094653): fusion-summon a chosen Fusion Monster using hand +
  // field materials. The target uid is the extra-deck Fusion to summon; the AI
  // (no target) falls back to the first available fusion.
  engineEffects.register({
    id: 24094653,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    canActivate: (api) => api.canFusionSummon(api.controller),
    canTarget: (api, uid) => api.canFusionSummon(api.controller, uid),
    execute: (api, targetUids) => {
      const uid = targetUids?.[0];
      if (uid && api.canFusionSummon(api.controller, uid)) api.fusionSummon(api.controller, uid);
      else api.fusionSummon(api.controller);
    },
  });

  // Dragon Capture Jar (50045299): continuous Trap. It may be flipped with no
  // Dragon on the field (preventive); while face-up, Dragons already there and
  // any that arrive later are switched to (and locked in) Defense Position.
  // Auto prompts only when the opponent has a face-up Dragon and no Jar is
  // already locking the board. A prompt on every summon or Battle Phase start
  // paused the duel with the card still Set. Clicking the Set card still
  // flips it (computeActivatableUids ignores relevantNow). `always` mode
  // still offers it in every window.
  engineEffects.register({
    id: 50045299,
    trigger: EngineEffectTrigger.CONTINUOUS,
    relevantNow: (api) => jarWorthFlipping(api),
    execute: (api) => {
      for (const seat of [0, 1] as Seat[]) {
        for (const m of getMonsters(api.state.zones[seat])) {
          if (m.faceUp && api.library.get(m.cardId)?.race === 'Dragon' && m.position !== CardPosition.DEFENSE) {
            const from = m.position;
            m.position = CardPosition.DEFENSE;
            api.emit({ type: EventType.POSITION_CHANGED, cardUid: m.uid, position: m.position });
            api.noteBattlePosition(m.uid, from);
          }
        }
      }
    },
  });

  // Burn spells.
  registerBurn(76103675, 200); // Sparks
  registerBurn(46130346, 500); // Hinotama
  registerBurn(73134081, 600); // Final Flame
  registerBurn(19523799, 800); // Ookazi

  // Healing spells.
  registerHeal(38199696, 500); // Red Medicine
  registerHeal(11868825, 600); // Goblin's Secret Remedy
  registerHeal(84257639, 1000); // Dian Keto the Cure Master

  // Equip spells: +ATK/+DEF to a face-up monster of the matching race/attribute.
  for (const equip of LOB_EQUIPS) registerEquip(equip);

  // Field spells: continuous +ATK/+DEF to matching races on both fields.
  for (const field of LOB_FIELD_SPELLS) registerFieldSpell(field);

  registerStarterDeckEffects();
  registerMetalRaidersWave1();
  registerMetalRaidersWave2();
  registerMetalRaidersWave3();
  registerTributeCosts();
}

/**
 * Metal Raiders, 26 June 2002: "This monster can attack your opponent's Life
 * Points directly." Same operation as EFFECT_DIRECT_ATTACK in
 * c98049915.lua, c12472242.lua, c58861941.lua, c32809211.lua, c21347810.lua,
 * and c5901497.lua. They may still attack a monster instead.
 */
const DIRECT_ATTACK_IDS = new Set<number>([
  98049915, // Mystic Lamp
  12472242, // Leghul
  58861941, // Ooguchi
  32809211, // Jinzo #7
  21347810, // Rainbow Flower
  5901497, // Queen's Double
]);

export function cardAttacksDirectly(cardId: number | string): boolean {
  return DIRECT_ATTACK_IDS.has(Number(cardId));
}

function registerMetalRaidersWave3(): void {
  // Direct attackers are the set above.
  // Time Wizard, Barrel Dragon, and Jirai Gumo toss coins in GameEngine.
  // Thunder Dragon, Sangan, Witch, Immortal of Thunder, and Sword of
  // Deep-Seated also resolve in GameEngine: a hand discard, or a send to
  // the Graveyard, has to see the zone the card left.
  //
  // The Immortal of Thunder, Metal Raiders: FLIP, gain 3000 LP. The 5000
  // loss is separate and fires whenever it leaves the field for the
  // Graveyard. c84926738.lua only applies the loss after the flip resolved;
  // the printed text does not.
  engineEffects.register({
    id: IMMORTAL_OF_THUNDER,
    trigger: EngineEffectTrigger.FLIP,
    execute: (api) => {
      api.changeLifePoints(api.controller, 3000);
    },
  });

  // Robbin' Goblin stays face-up. Each of your monsters' battle damage
  // makes the opponent discard; that trigger is in GameEngine.
  // Minimal asks at the start of your Battle Phase, and when the opponent
  // attacks a Set Defense monster of yours that would rebound damage.
  // Auto also asks when you declare an attack, or when their attack would
  // make one of your monsters inflict battle damage. Never the Damage Step:
  // EVENT_FREE_CHAIN, and era-rules.md only lets ATK/DEF modifiers in there.
  // The discard of a face-up copy is not a Trap activation, so Seven Tools
  // and Solemn Judgment do not answer it.
  engineEffects.register({
    id: ROBBIN_GOBLIN,
    trigger: EngineEffectTrigger.CONTINUOUS,
    relevantNow: robbinGoblinRelevantNow,
    execute: () => {
      /* battle-damage discard is noteBattleDamage */
    },
  });

  // Big Eye, Metal Raiders: FLIP, see the 5 cards on top of your Deck
  // and put them back in any order. c16768387.lua asks for a number
  // from 1 to 5; the printed line is the 5 cards, or fewer if the
  // Deck is shorter. The first card chosen is the new top.
  engineEffects.register({
    id: BIG_EYE,
    trigger: EngineEffectTrigger.FLIP,
    canActivate: (api) => api.state.zones[api.controller].deck.length > 0,
    execute: (api) => {
      api.orderDeckTop(5);
    },
  });

  // Shield & Sword, Metal Raiders: switch printed ATK and DEF of every
  // face-up monster until the turn ends. Modifiers stay on ATK or DEF.
  // A monster that was not face-up when this resolved is left alone.
  // c52097679.lua sets the base until the End Phase; a second copy
  // sets the same base again and does not switch them back.
  engineEffects.register({
    id: SHIELD_AND_SWORD,
    trigger: EngineEffectTrigger.ACTIVATE,
    canActivate: (api) =>
      ([0, 1] as Seat[]).some((seat) =>
        getMonsters(api.state.zones[seat]).some((monster) => monster.faceUp),
      ),
    execute: (api) => {
      api.swapPrintedStats();
    },
  });
}

/**
 * Discard and Tribute costs from Metal Raiders, 26 June 2002.
 * Tribute to the Doomed discards 1 card, then destroys 1 monster.
 * c79759861.lua. The Cheerful Coffin discards 1 to 3 monsters from
 * the hand when it resolves. c41142615.lua. Share the Pain Tributes
 * 1 of yours as the cost, then the opponent Tributes 1 for no effect.
 * c56830749.lua. The opponent's Tribute is resolved in GameEngine.
 */
function registerTributeCosts(): void {
  const doomedVictim = (api: EngineApi, uid: string): boolean => {
    const monster = targetableFieldMonsters(api).find((card) => card.uid === uid);
    return !!monster && !isDragonProtected(api, uid);
  };
  const handMonsters = (api: EngineApi): CardInstance[] =>
    api.state.zones[api.controller].hand.filter(
      (card) => cardKindOf(api, card) === CardKind.MONSTER,
    );

  engineEffects.register({
    id: TRIBUTE_TO_THE_DOOMED,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    canActivate: (api) => {
      if (!targetableFieldMonsters(api).some((monster) => !isDragonProtected(api, monster.uid))) {
        return false;
      }
      if (api.resolving()) return true;
      const hand = api.state.zones[api.controller].hand;
      if (hand.some((card) => card.uid === api.source.uid)) return hand.length > 1;
      return true;
    },
    canTarget: doomedVictim,
    destroys: (_api, targetUids) => (targetUids?.[0] ? [targetUids[0]] : []),
    execute: (api, targetUids) => {
      const uid = targetUids?.[0];
      if (!uid || !doomedVictim(api, uid)) return;
      if (!api.destroyCard(uid)) return;
      api.emit({
        type: EventType.MESSAGE,
        text: 'A monster was destroyed.',
        code: 'tributeDoomed',
        vars: { cardId: api.source.cardId },
      });
    },
  });

  engineEffects.register({
    id: CHEERFUL_COFFIN,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    minTargets: 1,
    maxTargets: 3,
    canActivate: (api) => api.resolving() || handMonsters(api).length > 0,
    canTarget: (api, uid) => handMonsters(api).some((card) => card.uid === uid),
    execute: (api, targetUids) => {
      const legal = new Set(handMonsters(api).map((card) => card.uid));
      const chosen = (targetUids ?? []).filter((uid) => legal.has(uid)).slice(0, 3);
      for (const uid of chosen) api.sendToGraveyard(uid);
      if (chosen.length === 0) return;
      api.emit({
        type: EventType.MESSAGE,
        text: `Discarded ${chosen.length} monster(s).`,
        code: 'cheerfulCoffin',
        vars: { cardId: api.source.cardId, n: chosen.length },
      });
    },
  });

  engineEffects.register({
    id: SHARE_THE_PAIN,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    costSelection: true,
    canActivate: (api) => {
      if (api.resolving()) return true;
      return (
        getMonsters(api.state.zones[api.controller]).length > 0 &&
        getMonsters(api.state.zones[otherSeat(api.controller)]).length > 0
      );
    },
    canTarget: (api, uid) =>
      getMonsters(api.state.zones[api.controller]).some((monster) => monster.uid === uid),
    execute: () => {
      /* Your Tribute is the cost. The opponent's Tribute is resolveShareThePain. */
    },
  });
}

/**
 * Metal Raiders, 26 June 2002.
 * Thunder Dragon: discard this card in your Main Phase to add 1 or 2
 * Thunder Dragons from your Deck. You need at least one copy to activate.
 * c31786629.lua selects 1 or 2. Not once per turn.
 * Sangan: sent from the field to the Graveyard, add 1 monster with ATK
 * 1500 or less. Metal Raiders (26 June 2002) says move 1 monster with
 * an ATK of 1500 or less from your Deck to your hand. c26202165.lua
 * also forbids activating that name and is once per turn; that errata
 * is not used.
 * Witch of the Black Forest: sent from the field, add 1 monster with DEF
 * 1500 or less. The "cannot activate that name" line in c78010363.lua
 * is the later errata.
 * Both searches are mandatory when a legal monster is in the Deck.
 * Solemn Judgment and Horn of Heaven negate the summon, so the monster
 * never entered the field and neither search starts. Trap Hole destroys
 * a summon that already succeeded, and the search still happens.
 */
export const THUNDER_DRAGON = 31786629;
export const SANGAN = 26202165;
export const WITCH_OF_THE_BLACK_FOREST = 78010363;
export const IMMORTAL_OF_THUNDER = 84926738;
export const SWORD_OF_DEEP_SEATED = 98495314;

/**
 * Metal Raiders, 26 June 2002. Mandatory when this monster inflicts battle
 * damage to the opponent (c15150365.lua, c10189126.lua, c71107816.lua).
 * Destroying a Defense Position monster with no Life Point loss does not
 * count. Waboku or Kuriboh reducing the damage to 0 does not either.
 * Not once per turn. An empty hand still lets the discard effect resolve.
 * Robbin' Goblin (c88279736.lua) is Continuous: each face-up copy triggers
 * when a monster you control inflicts that damage.
 */
export const WHITE_MAGICAL_HAT = 15150365;
export const MASKED_SORCERER = 10189126;
export const THE_BISTRO_BUTCHER = 71107816;
export const ROBBIN_GOBLIN = 88279736;

/**
 * Your Battle Phase start, your attack declaration, or their attack on a
 * monster of yours that would inflict battle damage. Never the Damage Step.
 */
function robbinGoblinRelevantNow(api: EngineApi): boolean {
  if (api.state.phase !== EnginePhase.BATTLE) return false;
  const attack = api.state.pendingAttack;
  if (attack && api.state.battleStep !== 'DAMAGE') {
    if (attack.seat === api.controller) return true;
    if (attack.targetUid) {
      const target = getMonsters(api.state.zones[api.controller]).find(
        (monster) => monster.uid === attack.targetUid,
      );
      const attacker = getMonsters(api.state.zones[attack.seat]).find(
        (monster) => monster.uid === attack.attackerUid,
      );
      if (target && attacker && defenderInflictsBattleDamage(api, attacker, target)) {
        return true;
      }
    }
  }
  if (api.state.turnPlayer !== api.controller) return false;
  if (api.state.battleStep !== 'START') return false;
  if (attack || api.state.chain.length > 0) return false;
  return getMonsters(api.state.zones[api.controller]).some(
    (monster) => monster.faceUp && monster.position === CardPosition.ATTACK,
  );
}

/** Rebound on Defense, or winning an ATK vs ATK clash. A Set is Defense. */
function defenderInflictsBattleDamage(
  api: EngineApi,
  attacker: CardInstance,
  target: CardInstance,
): boolean {
  const reverse = api.state.reverseStatMods;
  const atk = getModifiedAtk(attacker, api.library, reverse);
  const defense = !target.faceUp || target.position === CardPosition.DEFENSE;
  if (defense) {
    return getModifiedDef(target, api.library, reverse) > atk;
  }
  return getModifiedAtk(target, api.library, reverse) > atk;
}

/** Metal Raiders special-summon procedures. June 2002 printing. */
export const GATE_GUARDIAN = 25833572;
export const HARPIE_LADY = 76812113;
export const HARPIE_LADY_SISTERS = 12206212;
export const ELEGANT_EGOTIST = 90219263;
const GATE_PIECES = [25955164, 62340868, 98434877] as const;

/** Metal Raiders: Cocoon of Evolution, June 2002 printing. */
export const PETIT_MOTH = 58192742;
export const COCOON_OF_EVOLUTION = 40240595;
export const LARVAE_MOTH = 87756343;
export const GREAT_MOTH = 14141448;
/** Larvae Moth: the 2nd of your turns after Cocoon was equipped. */
export const LARVAE_COCOON_TURN = 2;
/** Great Moth: the 4th of your turns after Cocoon was equipped. */
export const GREAT_COCOON_TURN = 4;

/** Cannot be Normal Summoned or Set. Sisters only via Elegant Egotist. */
export function cannotNormalSummon(cardId: number | string): boolean {
  const id = Number(cardId);
  return (
    id === GATE_GUARDIAN ||
    id === HARPIE_LADY_SISTERS ||
    id === LARVAE_MOTH ||
    id === GREAT_MOTH
  );
}

/** 2 for Larvae Moth, 4 for Great Moth, otherwise null. */
export function mothEvolveTurn(cardId: number | string): number | null {
  const id = Number(cardId);
  if (id === LARVAE_MOTH) return LARVAE_COCOON_TURN;
  if (id === GREAT_MOTH) return GREAT_COCOON_TURN;
  return null;
}

/** Face-up Petit Moth on either field. The 2002 text does not say "you control". */
export function faceUpPetitMoths(state: MatchState): CardInstance[] {
  const out: CardInstance[] = [];
  for (const seat of [0, 1] as const) {
    for (const monster of getMonsters(state.zones[seat])) {
      if (monster.faceUp && Number(monster.cardId) === PETIT_MOTH) out.push(monster);
    }
  }
  return out;
}

/** Your face-up Cocoon of Evolution equipped to this monster. */
export function cocoonOnMoth(
  state: MatchState,
  moth: CardInstance,
  controller: Seat,
): CardInstance | null {
  for (const uid of moth.equippedUids) {
    const found = findCard(state, uid);
    if (!found || found.location.seat !== controller) continue;
    if (found.location.zone !== 'spellTraps' || !found.instance.faceUp) continue;
    if (Number(found.instance.cardId) !== COCOON_OF_EVOLUTION) continue;
    return found.instance;
  }
  return null;
}

/**
 * Your Petit Moth whose Cocoon counter is exactly `turn`.
 * That turn only: the 2nd does not stay open for Larvae, nor the 4th for Great Moth.
 */
export function evolveTribute(
  state: MatchState,
  seat: Seat,
  turn: number,
): { moth: CardInstance; cocoon: CardInstance } | null {
  for (const moth of getMonsters(state.zones[seat])) {
    if (!moth.faceUp || Number(moth.cardId) !== PETIT_MOTH) continue;
    const cocoon = cocoonOnMoth(state, moth, seat);
    if (!cocoon || cocoon.turnCounter !== turn) continue;
    return { moth, cocoon };
  }
  return null;
}

export function isGatePiece(cardId: number | string): boolean {
  return (GATE_PIECES as readonly number[]).includes(Number(cardId));
}

/**
 * One Sanga, one Kazejin and one Suijin. The first copy of each name.
 * Null when any of the three is missing.
 */
export function gateTributeSet(
  monsters: readonly { uid: string; cardId: string }[],
): string[] | null {
  const picked: string[] = [];
  for (const code of GATE_PIECES) {
    const found = monsters.find(
      (monster) => Number(monster.cardId) === code && !picked.includes(monster.uid),
    );
    if (!found) return null;
    picked.push(found.uid);
  }
  return picked;
}

/** The three uids are one of each guardian, and each uid is one of those monsters. */
export function isGateTributeSet(
  monsters: readonly { uid: string; cardId: string }[],
  tributeUids: readonly string[],
): boolean {
  if (tributeUids.length !== 3 || new Set(tributeUids).size !== 3) return false;
  const codes = tributeUids.map((uid) => {
    const monster = monsters.find((entry) => entry.uid === uid);
    return monster ? Number(monster.cardId) : 0;
  });
  return GATE_PIECES.every((code) => codes.includes(code));
}

function egotistMonster(cardId: number | string): boolean {
  const id = Number(cardId);
  return id === HARPIE_LADY || id === HARPIE_LADY_SISTERS;
}

/** A face-up Harpie Lady on either field, a free zone, and a Lady or Sisters in hand or Deck. */
export function egotistReady(api: EngineApi): boolean {
  const lady = ([0, 1] as const).some((seat) =>
    getMonsters(api.state.zones[seat]).some(
      (monster) => monster.faceUp && Number(monster.cardId) === HARPIE_LADY,
    ),
  );
  if (!lady || !api.hasFreeMonsterZone(api.controller)) return false;
  const zone = api.state.zones[api.controller];
  return [...zone.hand, ...zone.deck].some((card) => egotistMonster(card.cardId));
}

export type GraveSearch = 'sangan' | 'witch';

export function handIgnitionOf(cardId: number | string): 'thunderDragon' | null {
  return Number(cardId) === THUNDER_DRAGON ? 'thunderDragon' : null;
}

export function graveSearchOf(cardId: number | string): GraveSearch | null {
  const id = Number(cardId);
  if (id === SANGAN) return 'sangan';
  if (id === WITCH_OF_THE_BLACK_FOREST) return 'witch';
  return null;
}

/**
 * Metal Raiders, 26 June 2002.
 * Time Wizard: call Heads or Tails. Right destroys the opponent's monsters.
 * Wrong destroys yours and you lose LP equal to half their face-up ATK.
 * c71625222.lua does the opposite on a wrong call; the printed text wins.
 * Barrel Dragon: select 1 opposing monster, then toss 3 times.
 * 2 or 3 heads destroys that monster. c81480460.lua selects the
 * target before TossCoin. The client text puts the selection before
 * the semicolon.
 * Once per turn, your Main Phase (the first erratum names that phase).
 */
export const TIME_WIZARD = 71625222;
export const BARREL_DRAGON = 81480460;
/** Jirai Gumo: on attack, call the coin. A wrong call halves your LP, then the attack goes on. c94773007.lua halves LP on a right call; the printed text wins. */
export const JIRAI_GUMO = 94773007;

export type CoinIgnition = 'timeWizard' | 'barrel';

export function coinIgnitionOf(cardId: number | string): CoinIgnition | null {
  const id = Number(cardId);
  if (id === TIME_WIZARD) return 'timeWizard';
  if (id === BARREL_DRAGON) return 'barrel';
  return null;
}

export function declaresAttackWithCoin(cardId: number | string): boolean {
  return Number(cardId) === JIRAI_GUMO;
}

/**
 * Metal Raiders, 26 June 2002. Ignitions that Tribute a monster.
 * Cannon Soldier: Tribute 1 monster you control, including itself, for
 * 500 damage. Not once per turn. c11384280.lua has no count limit.
 * Catapult Turtle: Tribute 1 monster with ATK greater than 0, including
 * itself. Damage is half that ATK, rounded down. The later "once per
 * turn" line is not this printing. c95727991.lua adds that limit.
 * The Little Swordsman of Aile: Tribute 1 other monster. This card gains
 * 700 ATK until the End Phase. Not once per turn. c25109950.lua.
 * Soul Exchange (c68005187.lua, EFFECT_EXTRA_RELEASE) adds the selected
 * monster to that Tribute. Tributing it is optional. A face-down selection
 * has no known ATK, so Catapult Turtle cannot Tribute it.
 */
export const CANNON_SOLDIER = 11384280;
export const CATAPULT_TURTLE = 95727991;
export const LITTLE_SWORDSMAN = 25109950;
export const BLAST_JUGGLER = 70138455;
export const TRIBUTE_TO_THE_DOOMED = 79759861;
export const CHEERFUL_COFFIN = 41142615;
export const SHARE_THE_PAIN = 56830749;
export const MUSHROOM_MAN_2 = 93900406;
/** Big Eye. FLIP: look at the top 5 of your Deck and reorder them. */
export const BIG_EYE = 16768387;
/** Shield & Sword. Switch printed ATK and DEF until the turn ends. */
export const SHIELD_AND_SWORD = 52097679;

export type TributeIgnition = 'cannon' | 'turtle' | 'aile';

export function tributeIgnitionOf(cardId: number | string): TributeIgnition | null {
  const id = Number(cardId);
  if (id === CANNON_SOLDIER) return 'cannon';
  if (id === CATAPULT_TURTLE) return 'turtle';
  if (id === LITTLE_SWORDSMAN) return 'aile';
  return null;
}

/**
 * Monsters this ignition may Tribute. Aile cannot Tribute itself.
 * A Soul Exchange selection counts as a monster you control for the Tribute.
 * Catapult Turtle still needs ATK above 0, so a face-down selection is out.
 */
export function tributeCandidateUids(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  sourceUid: string,
  kind: TributeIgnition,
): string[] {
  const mine = getMonsters(state.zones[seat]);
  const exchanged = soulExchangeTributeUids(state, seat)
    .map((uid) => findCard(state, uid)?.instance)
    .filter((monster): monster is CardInstance => monster != null);
  const atkAboveZero = (monster: CardInstance): boolean =>
    getModifiedAtk(monster, library, state.reverseStatMods) > 0;

  if (kind === 'aile') {
    return [...mine.filter((monster) => monster.uid !== sourceUid), ...exchanged].map(
      (monster) => monster.uid,
    );
  }
  if (kind === 'turtle') {
    return [
      ...mine.filter(atkAboveZero),
      ...exchanged.filter((monster) => monster.faceUp && atkAboveZero(monster)),
    ].map((monster) => monster.uid);
  }
  return [...mine, ...exchanged].map((monster) => monster.uid);
}

/**
 * SDY/SDK unique effects. 2002 TCG text, not later PSCT errata.
 */
function registerStarterDeckEffects(): void {
  // Trap Master: FLIP — look at 1 Set card (or a face-up Trap) and
  // destroy it if it is a Trap (same as Reaper of the Cards).
  engineEffects.register({
    id: STARTER_IDS.TRAP_MASTER,
    trigger: EngineEffectTrigger.FLIP,
    requiresTarget: true,
    canActivate: (api) => revealTargets(api, CardKind.TRAP).length > 0,
    canTarget: (api, uid) => revealTargets(api, CardKind.TRAP).some((c) => c.uid === uid),
    destroys: (api, targetUids) => targetedTrapUid(api, targetUids),
    execute: (api, targetUids) => resolveReveal(api, targetUids, CardKind.TRAP),
  });

  // Reinforcements / Castle Walls: +500 ATK or DEF until End Phase.
  // 2002 printed English said "a selected monster"; the first erratum
  // ("1 face-up monster") is a clarification, not a later rewrite.
  // Face-down monsters have no public ATK/DEF, so they are not legal.
  const canBoostMonster = (api: EngineApi, uid: string): boolean => {
    const m = findFieldMonster(api, uid);
    return !!m && m.faceUp && !isDragonProtected(api, uid);
  };
  const pickBoostTarget = (
    api: EngineApi,
    targetUids: string[] | undefined,
  ): CardInstance | undefined => {
    const uid = targetUids?.[0];
    const chosen = uid ? findFieldMonster(api, uid) : null;
    if (chosen && canBoostMonster(api, chosen.uid)) return chosen;
    const pending = api.state.pendingAttack;
    if (pending) {
      const ownUid =
        pending.seat === api.controller ? pending.attackerUid : pending.targetUid;
      if (ownUid && canBoostMonster(api, ownUid)) {
        const involved = findFieldMonster(api, ownUid);
        if (involved) return involved;
      }
    }
    const own = faceUpMonsters(api, api.controller).filter(
      (m) => !isDragonProtected(api, m.uid),
    );
    if (own.length === 0) return undefined;
    return own.reduce((best, m) => (atkOf(api, m) > atkOf(api, best) ? m : best));
  };

  engineEffects.register({
    id: STARTER_IDS.REINFORCEMENTS,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    damageStep: true,
    relevantNow: relevantInBattleOrDoomed,
    canActivate: (api) =>
      faceUpFieldMonsters(api).some((m) => !isDragonProtected(api, m.uid)),
    canTarget: canBoostMonster,
    execute: (api, targetUids) => {
      const target = pickBoostTarget(api, targetUids);
      if (!target) return;
      api.addStatModifier(target.uid, 500, 0, `untilEnd:${api.source.uid}`);
    },
  });

  engineEffects.register({
    id: STARTER_IDS.CASTLE_WALLS,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    damageStep: true,
    relevantNow: relevantInBattleOrDoomed,
    canActivate: (api) =>
      faceUpFieldMonsters(api).some((m) => !isDragonProtected(api, m.uid)),
    canTarget: canBoostMonster,
    execute: (api, targetUids) => {
      const target = pickBoostTarget(api, targetUids);
      if (!target) return;
      api.addStatModifier(target.uid, 0, 500, `untilEnd:${api.source.uid}`);
    },
  });

  // Waboku: 0 battle damage and your monsters survive battle this turn
  // (printed errata text; see docs/era-rules.md).
  engineEffects.register({
    id: STARTER_IDS.WABOKU,
    trigger: EngineEffectTrigger.ACTIVATE,
    relevantNow: relevantInBattleOrDoomed,
    execute: (api) => {
      api.setBattleDamageImmune();
      api.setBattleDestroyImmune();
    },
  });

  // Card Destruction (72892473): both players discard the hand and draw that many.
  engineEffects.register({
    id: STARTER_IDS.CARD_DESTRUCTION,
    trigger: EngineEffectTrigger.ACTIVATE,
    canActivate: (api) =>
      api.state.zones[api.controller].hand.length > 0 ||
      api.state.zones[otherSeat(api.controller)].hand.length > 0,
    execute: (api) => {
      const you = api.controller;
      const opp = otherSeat(api.controller);
      const nYou = api.discardHand(you);
      const nOpp = api.discardHand(opp);
      if (nYou > 0) api.drawCards(you, nYou);
      if (nOpp > 0) api.drawCards(opp, nOpp);
    },
  });

  // Soul Exchange (68005187): each activation marks 1 opponent monster you may
  // Tribute this turn (a second copy marks another). Skip the Battle Phase.
  engineEffects.register({
    id: STARTER_IDS.SOUL_EXCHANGE,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    canActivate: (api) =>
      getMonsters(api.state.zones[otherSeat(api.controller)]).some(
        (m) => !isDragonProtected(api, m.uid),
      ),
    canTarget: (api, uid) => {
      const found = findCard(api.state, uid);
      return (
        !!found &&
        found.location.zone === 'monsters' &&
        found.location.seat === otherSeat(api.controller) &&
        !isDragonProtected(api, uid)
      );
    },
    execute: (api, targetUids) => {
      const uid = targetUids?.[0];
      const found = uid ? findCard(api.state, uid) : null;
      const target =
        found && found.location.zone === 'monsters'
          ? found.instance
          : getMonsters(api.state.zones[otherSeat(api.controller)]).find(
              (m) => !isDragonProtected(api, m.uid),
            );
      if (!target) return;
      api.markSoulExchange(target.uid);
      api.skipBattlePhase();
    },
  });

  // Last Will (85602018): once for each copy activated this turn, if a monster
  // you control is sent from your Monster Zone to your Graveyard — earlier
  // this turn or later — you may Special Summon a monster with 1500 ATK or
  // less from your Deck. Declining waits until a later phase. The turn it
  // was activated is the only turn it applies.
  engineEffects.register({
    id: STARTER_IDS.LAST_WILL,
    trigger: EngineEffectTrigger.ACTIVATE,
    execute: (api) => api.armLastWill(),
  });

  // Ultimate Offering (80604091): Continuous Trap. First flip stays. Paying 500
  // for an extra Normal Summon is handled in GameEngine while it is face-up.
  // Do not offer the flip (or any prompt) with no monster in hand to Summon/Set.
  engineEffects.register({
    id: STARTER_IDS.ULTIMATE_OFFERING,
    trigger: EngineEffectTrigger.CONTINUOUS,
    canActivate: (api) =>
      handHasNormalSummonMonster(api.state.zones[api.controller], api.library),
    execute: () => {
      /* ignition while face-up is in GameEngine.igniteUltimateOffering */
    },
  });

  // The Flute of Summoning Dragon (43973174): if you control Lord of D., SS up
  // to 2 Dragons from the hand.
  engineEffects.register({
    id: STARTER_IDS.FLUTE,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    minTargets: 1,
    maxTargets: 2,
    canActivate: (api) => {
      if (!controlsLordOfD(api, api.controller)) return false;
      if (!api.hasFreeMonsterZone(api.controller)) return false;
      return handDragons(api).length > 0;
    },
    canTarget: (api, uid) => handDragons(api).some((c) => c.uid === uid),
    canTargetSet: (api, uids) => {
      const legal = new Set(handDragons(api).map((c) => c.uid));
      const free = api.state.zones[api.controller].monsters.filter((c) => c === null).length;
      return (
        uids.length >= 1 &&
        uids.length <= Math.min(2, free) &&
        new Set(uids).size === uids.length &&
        uids.every((u) => legal.has(u))
      );
    },
    execute: (api, targetUids) => {
      const legal = handDragons(api);
      let picked = (targetUids ?? []).filter((uid) => legal.some((c) => c.uid === uid));
      if (picked.length === 0) {
        picked = legal.slice(0, Math.min(2, legal.length)).map((c) => c.uid);
      }
      const free = api.state.zones[api.controller].monsters.filter((c) => c === null).length;
      for (const uid of picked.slice(0, Math.min(2, free))) {
        if (!api.hasFreeMonsterZone(api.controller)) break;
        api.specialSummon(uid, api.controller, api.summonPositionFor(uid));
      }
    },
  });

  // Reverse Trap (77622396): invert ATK/DEF add/subtract modifiers this turn.
  // A second copy the same turn does nothing (era FAQ), so do not offer it.
  // It changes stats, so it shares the Damage Step timing of the boost traps.
  engineEffects.register({
    id: STARTER_IDS.REVERSE_TRAP,
    trigger: EngineEffectTrigger.ACTIVATE,
    damageStep: true,
    relevantNow: relevantInBattleOrDoomed,
    canActivate: (api) => !api.state.reverseStatMods,
    execute: (api) => api.setReverseStatMods(),
  });

  // The Stern Mystic: FLIP — look at all Set cards; do not destroy.
  engineEffects.register({
    id: STARTER_IDS.STERN_MYSTIC,
    trigger: EngineEffectTrigger.FLIP,
    execute: (api) => {
      const cards: { uid: string; cardId: string }[] = [];
      for (const seat of [0, 1] as Seat[]) {
        const z = api.state.zones[seat];
        for (const c of [...z.monsters, ...z.spellTraps, z.fieldSpell]) {
          if (c && !c.faceUp) cards.push({ uid: c.uid, cardId: c.cardId });
        }
      }
      api.peekCards(cards, { reveal: true });
    },
  });

  // The Inexperienced Spy: look at 1 card in the opponent's hand.
  engineEffects.register({
    id: STARTER_IDS.SPY,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    canActivate: (api) => api.state.zones[otherSeat(api.controller)].hand.length > 0,
    canTarget: (api, uid) =>
      api.state.zones[otherSeat(api.controller)].hand.some((c) => c.uid === uid),
    execute: (api, targetUids) => {
      const hand = api.state.zones[otherSeat(api.controller)].hand;
      const uid = targetUids?.[0];
      const card = (uid ? hand.find((c) => c.uid === uid) : undefined) ?? hand[0];
      if (card) api.peekCards([{ uid: card.uid, cardId: card.cardId }]);
    },
  });

  // Ancient Telescope (17092736): look at the top 5 of the opponent's Deck.
  engineEffects.register({
    id: STARTER_IDS.TELESCOPE,
    trigger: EngineEffectTrigger.ACTIVATE,
    canActivate: (api) => api.state.zones[otherSeat(api.controller)].deck.length > 0,
    execute: (api) => {
      const deck = api.state.zones[otherSeat(api.controller)].deck;
      api.peekCards(deck.slice(0, 5).map((c) => ({ uid: c.uid, cardId: c.cardId })));
    },
  });

  // Just Desserts (24068492): 500 damage per monster the opponent controls.
  // No `relevantNow`: Auto and Always still offer it in any open window, and
  // the AI still burns on an attack. Minimal's one extra pause, at the start
  // of the opponent's Battle Phase, lives in GameEngine.
  engineEffects.register({
    id: STARTER_IDS.JUST_DESSERTS,
    trigger: EngineEffectTrigger.ACTIVATE,
    canActivate: (api) => getMonsters(api.state.zones[otherSeat(api.controller)]).length > 0,
    execute: (api) => {
      const n = getMonsters(api.state.zones[otherSeat(api.controller)]).length;
      if (n > 0) api.changeLifePoints(otherSeat(api.controller), -500 * n);
    },
  });
}

/**
 * Metal Raiders wave 1. 26 June 2002 printed text. One-shot effects only.
 * Spell Speed stays 1 for these Spells and 2 for Mirror Force.
 */
function registerMetalRaidersWave1(): void {
  const TREMENDOUS_FIRE = 46918794;
  const HEAVY_STORM = 19613556;
  const BLOCK_ATTACK = 25880422;
  const MIRROR_FORCE = 44095762;
  const SOUL_RELEASE = 5758500;
  const MAGICIAN_OF_FAITH = 31560081;
  const MASK_OF_DARKNESS = 28933734;
  const PRINCESS = 51371017;
  const DRAGON_PIPER = 55763552;

  const bothGraveyards = (api: EngineApi): CardInstance[] => [
    ...api.state.zones[0].graveyard,
    ...api.state.zones[1].graveyard,
  ];

  const ownGyOfKind = (api: EngineApi, kind: CardKind): CardInstance[] =>
    api.state.zones[api.controller].graveyard.filter((c) => cardKindOf(api, c) === kind);

  const spellTrapCount = (api: EngineApi, seat: Seat): number => {
    const zones = api.state.zones[seat];
    return getSpellTraps(zones).length + (zones.fieldSpell ? 1 : 0);
  };

  const faceUpJars = (api: EngineApi): CardInstance[] =>
    ([0, 1] as Seat[]).flatMap((seat) =>
      getSpellTraps(api.state.zones[seat]).filter(
        (c) => c.faceUp && Number(c.cardId) === DRAGON_CAPTURE_JAR,
      ),
    );

  // Tremendous Fire: 1000 to the opponent, 500 to you.
  engineEffects.register({
    id: TREMENDOUS_FIRE,
    trigger: EngineEffectTrigger.ACTIVATE,
    execute: (api) => {
      api.changeLifePoints(otherSeat(api.controller), -1000);
      api.changeLifePoints(api.controller, -500);
    },
  });

  // Heavy Storm: destroy every other Spell and Trap on the field, both
  // sides, including the Field Spell. Illegal when it would destroy nothing.
  // The card itself goes to the GY after resolution.
  engineEffects.register({
    id: HEAVY_STORM,
    trigger: EngineEffectTrigger.ACTIVATE,
    canActivate: (api) =>
      api.resolving() || spellTrapsOnField(api, api.source.uid).length > 0,
    destroys: (api) => spellTrapsOnField(api, api.source.uid).map((c) => c.uid),
    execute: (api) => {
      for (const card of spellTrapsOnField(api, api.source.uid)) api.destroyCard(card.uid);
    },
  });

  // Block Attack: one face-up Attack Position monster the opponent controls
  // becomes Defense Position. It does not flip a face-down monster.
  engineEffects.register({
    id: BLOCK_ATTACK,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    canActivate: (api) =>
      getMonsters(api.state.zones[otherSeat(api.controller)]).some(
        (m) => m.faceUp && m.position === CardPosition.ATTACK && !isDragonProtected(api, m.uid),
      ),
    canTarget: (api, uid) => {
      const found = findCard(api.state, uid);
      return (
        !!found &&
        found.location.zone === 'monsters' &&
        found.location.seat === otherSeat(api.controller) &&
        found.instance.faceUp &&
        found.instance.position === CardPosition.ATTACK &&
        !isDragonProtected(api, uid)
      );
    },
    execute: (api, targetUids) => {
      const uid = targetUids?.[0];
      const found = uid ? findCard(api.state, uid) : null;
      const m =
        found &&
        found.location.zone === 'monsters' &&
        found.instance.faceUp &&
        found.instance.position === CardPosition.ATTACK
          ? found.instance
          : getMonsters(api.state.zones[otherSeat(api.controller)]).find(
              (mon) => mon.faceUp && mon.position === CardPosition.ATTACK && !isDragonProtected(api, mon.uid),
            );
      if (!m) return;
      const from = m.position;
      m.position = CardPosition.DEFENSE;
      m.positionChangedThisTurn = true;
      api.emit({ type: EventType.POSITION_CHANGED, cardUid: m.uid, position: m.position });
      api.noteBattlePosition(m.uid, from);
    },
  });

  // Mirror Force (Spell Speed 2): when an opponent's monster declares an
  // attack, destroy all of that player's Attack Position monsters.
  engineEffects.register({
    id: MIRROR_FORCE,
    trigger: EngineEffectTrigger.ACTIVATE,
    relevantNow: (api) => {
      const pending = api.state.pendingAttack;
      return !!pending && pending.seat !== api.controller;
    },
    canActivate: (api) => {
      const pending = api.state.pendingAttack;
      if (!pending || pending.seat === api.controller) return false;
      return getMonsters(api.state.zones[pending.seat]).some(
        (m) => m.faceUp && m.position === CardPosition.ATTACK,
      );
    },
    execute: (api) => {
      const pending = api.state.pendingAttack;
      const seat = pending && pending.seat !== api.controller ? pending.seat : otherSeat(api.controller);
      for (const m of getMonsters(api.state.zones[seat])) {
        if (m.faceUp && m.position === CardPosition.ATTACK) api.destroyCard(m.uid);
      }
    },
  });

  // Soul Release: banish up to 5 cards from either Graveyard.
  engineEffects.register({
    id: SOUL_RELEASE,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    minTargets: 1,
    maxTargets: 5,
    canActivate: (api) => bothGraveyards(api).length > 0,
    canTarget: (api, uid) => bothGraveyards(api).some((c) => c.uid === uid),
    canTargetSet: (api, uids) => {
      if (uids.length < 1 || uids.length > 5) return false;
      const legal = new Set(bothGraveyards(api).map((c) => c.uid));
      return new Set(uids).size === uids.length && uids.every((u) => legal.has(u));
    },
    execute: (api, targetUids) => {
      const legal = bothGraveyards(api);
      const chosen = (targetUids ?? []).filter((uid) => legal.some((c) => c.uid === uid));
      const toBanish = (chosen.length > 0 ? chosen : legal.slice(0, 5).map((c) => c.uid)).slice(0, 5);
      for (const uid of toBanish) api.banishCard(uid);
    },
  });

  const registerGyToHand = (id: number, kind: CardKind): void => {
    engineEffects.register({
      id,
      trigger: EngineEffectTrigger.FLIP,
      requiresTarget: true,
      canActivate: (api) => ownGyOfKind(api, kind).length > 0,
      canTarget: (api, uid) => ownGyOfKind(api, kind).some((c) => c.uid === uid),
      execute: (api, targetUids) => {
        const pool = ownGyOfKind(api, kind);
        const uid = targetUids?.[0];
        const picked = (uid ? pool.find((c) => c.uid === uid) : undefined) ?? pool[0];
        if (picked) api.returnToHand(picked.uid);
      },
    });
  };

  // Magician of Faith / Mask of Darkness: FLIP, return 1 of your GY Spells or Traps.
  registerGyToHand(MAGICIAN_OF_FAITH, CardKind.SPELL);
  registerGyToHand(MASK_OF_DARKNESS, CardKind.TRAP);

  // Princess of Tsurugi: FLIP, 500 damage per Spell or Trap the opponent controls.
  engineEffects.register({
    id: PRINCESS,
    trigger: EngineEffectTrigger.FLIP,
    execute: (api) => {
      const n = spellTrapCount(api, otherSeat(api.controller));
      if (n > 0) api.changeLifePoints(otherSeat(api.controller), -500 * n);
    },
  });

  // Dragon Piper: FLIP, destroy every face-up Dragon Capture Jar. If one
  // was destroyed, face-up Dragons switch to Attack Position.
  engineEffects.register({
    id: DRAGON_PIPER,
    trigger: EngineEffectTrigger.FLIP,
    canActivate: (api) => faceUpJars(api).length > 0,
    destroys: (api) => faceUpJars(api).map((c) => c.uid),
    execute: (api) => {
      const before = faceUpJars(api).map((c) => c.uid);
      if (before.length === 0) return;
      for (const uid of before) api.destroyCard(uid);
      const remaining = new Set(faceUpJars(api).map((c) => c.uid));
      if (before.every((uid) => remaining.has(uid))) return;
      for (const m of fieldMonsters(api)) {
        if (!m.faceUp || m.position === CardPosition.ATTACK) continue;
        if (api.library.get(m.cardId)?.race !== 'Dragon') continue;
        const from = m.position;
        m.position = CardPosition.ATTACK;
        m.positionChangedThisTurn = true;
        api.emit({ type: EventType.POSITION_CHANGED, cardUid: m.uid, position: m.position });
        api.noteBattlePosition(m.uid, from);
      }
    },
  });
}

const SOLEMN_JUDGMENT = 41420027;
const MAGIC_JAMMER = 77414722;
const SEVEN_TOOLS = 3819470;
const HORN_OF_HEAVEN = 98069388;
const FAKE_TRAP = 3027001;
const KURIBOH = 40640057;
const GUARDIANS = [25955164, 62340868, 98434877];

function lastChainLink(api: EngineApi) {
  return api.state.chain[api.state.chain.length - 1] ?? null;
}

function lastChainCard(api: EngineApi): CardInstance | null {
  const link = lastChainLink(api);
  if (!link) return null;
  return findCard(api.state, link.sourceUid)?.instance ?? null;
}

/**
 * Solemn / Magic Jammer / Seven Tools answer the activation of a Spell or
 * Trap Card. A trigger on a card that is already in play is not that
 * activation: Sword of Deep-Seated returning from the Graveyard, or a
 * face-up Robbin' Goblin discarding after battle damage.
 */
function lastActivationLink(api: EngineApi) {
  const link = lastChainLink(api);
  if (!link || link.triggerKind) return null;
  return link;
}

/**
 * The previous chain link belongs to the opponent. When `kind` is set, that
 * link's card must also be of that kind. Auto and Minimal use this so a
 * Counter Trap is not offered against the player's own Spell or Trap.
 */
function lastLinkIsOpponents(api: EngineApi, kind?: CardKind): boolean {
  const link = lastActivationLink(api);
  if (!link || link.controller === api.controller) return false;
  if (!kind) return true;
  const card = lastChainCard(api);
  return !!card && cardKindOf(api, card) === kind;
}

/**
 * The summon that opened this window is the opponent's monster.
 */
function opponentSummonInWindow(api: EngineApi): boolean {
  const summoned = api.responseTrigger;
  if (!summoned) return false;
  const found = findCard(api.state, summoned.uid);
  return !!found && found.location.seat !== api.controller;
}

/**
 * The previous chain link would destroy this card. Waboku and the battle
 * boosts chain to that operation, not to a list of passcodes.
 */
function lastLinkWouldDestroyThis(api: EngineApi): boolean {
  const link = api.state.chain[api.state.chain.length - 1];
  if (!link) return false;
  return (link.destroyUids ?? []).includes(api.source.uid);
}

/**
 * The previous chain link would destroy a Trap in this player's Spell/Trap
 * zone. Fake Trap answers that operation, not a list of passcodes.
 */
function lastLinkDestroysYourTrap(api: EngineApi): boolean {
  const link = api.state.chain[api.state.chain.length - 1];
  if (!link || link.controller === api.controller) return false;
  for (const uid of link.destroyUids ?? []) {
    if (uid === api.source.uid) continue;
    const found = findCard(api.state, uid);
    if (!found || found.location.seat !== api.controller) continue;
    if (found.location.zone !== 'spellTraps') continue;
    if (cardKindOf(api, found.instance) !== CardKind.TRAP) continue;
    return true;
  }
  return false;
}

/**
 * Battle damage this seat would take from the attack in progress.
 * Only the opponent's turn, and only when their monster is the attacker.
 * Attacking a Defense Position monster does not damage its controller.
 */
function incomingBattleDamage(api: EngineApi): number {
  if (api.state.turnPlayer === api.controller) return 0;
  if (api.state.battleDamageImmune[api.controller]) return 0;
  const attack = api.state.pendingAttack;
  if (!attack || attack.seat === api.controller) return 0;
  const attacker = findCard(api.state, attack.attackerUid)?.instance;
  if (!attacker) return 0;
  const atk = getModifiedAtk(attacker, api.library, api.state.reverseStatMods);
  if (!attack.targetUid) return atk;
  const target = findCard(api.state, attack.targetUid)?.instance;
  if (!target || target.position === CardPosition.DEFENSE) return 0;
  const targetAtk = getModifiedAtk(target, api.library, api.state.reverseStatMods);
  return atk > targetAtk ? atk - targetAtk : 0;
}

function registerMetalRaidersWave2(): void {
  const inDamage = (api: EngineApi): boolean => api.state.battleStep === 'DAMAGE';

  engineEffects.register({
    id: SOLEMN_JUDGMENT,
    trigger: EngineEffectTrigger.ACTIVATE,
    relevantNow: (api) =>
      lastLinkIsOpponents(api, CardKind.SPELL) ||
      lastLinkIsOpponents(api, CardKind.TRAP) ||
      opponentSummonInWindow(api),
    canActivate: (api) => {
      if (api.resolving()) return true;
      if (api.state.zones[api.controller].lifePoints < 1) return false;
      const last = lastActivationLink(api);
      if (last) {
        const card = lastChainCard(api);
        if (!card) return false;
        const kind = cardKindOf(api, card);
        return kind === CardKind.SPELL || kind === CardKind.TRAP;
      }
      return !!api.responseTrigger;
    },
    execute: (api) => {
      if (api.hasPreviousLink()) api.negatePreviousLink();
      else api.negateSummon();
    },
  });

  engineEffects.register({
    id: MAGIC_JAMMER,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    costSelection: true,
    relevantNow: (api) => lastLinkIsOpponents(api, CardKind.SPELL),
    canActivate: (api) => {
      if (api.resolving()) return true;
      const last = lastActivationLink(api);
      const card = last ? lastChainCard(api) : null;
      if (!card || cardKindOf(api, card) !== CardKind.SPELL) return false;
      return api.state.zones[api.controller].hand.length > 0;
    },
    canTarget: (api, uid) =>
      api.state.zones[api.controller].hand.some((c) => c.uid === uid),
    execute: (api) => {
      api.negatePreviousLink();
    },
  });

  engineEffects.register({
    id: SEVEN_TOOLS,
    trigger: EngineEffectTrigger.ACTIVATE,
    relevantNow: (api) => lastLinkIsOpponents(api, CardKind.TRAP),
    canActivate: (api) => {
      if (api.resolving()) return true;
      if (api.state.zones[api.controller].lifePoints < 1000) return false;
      const last = lastActivationLink(api);
      const card = last ? lastChainCard(api) : null;
      return !!card && cardKindOf(api, card) === CardKind.TRAP;
    },
    execute: (api) => {
      api.negatePreviousLink();
    },
  });

  engineEffects.register({
    id: HORN_OF_HEAVEN,
    trigger: EngineEffectTrigger.ACTIVATE,
    requiresTarget: true,
    costSelection: true,
    relevantNow: (api) => {
      const kind = api.summonWindow();
      return (kind === 'normal' || kind === 'flip') && opponentSummonInWindow(api);
    },
    canActivate: (api) => {
      if (api.resolving()) return true;
      const kind = api.summonWindow();
      if (kind !== 'normal' && kind !== 'flip') return false;
      if (!api.responseTrigger) return false;
      return getMonsters(api.state.zones[api.controller]).length > 0;
    },
    canTarget: (api, uid) =>
      getMonsters(api.state.zones[api.controller]).some((m) => m.uid === uid),
    execute: (api) => {
      api.negateSummon();
    },
  });

  engineEffects.register({
    id: FAKE_TRAP,
    trigger: EngineEffectTrigger.ACTIVATE,
    relevantNow: (api) => lastLinkDestroysYourTrap(api),
    canActivate: (api) => api.resolving() || lastLinkDestroysYourTrap(api),
    execute: (api) => {
      api.armFakeTrap();
    },
  });

  engineEffects.register({
    id: KURIBOH,
    trigger: EngineEffectTrigger.ACTIVATE,
    damageStep: true,
    relevantNow: (api) => inDamage(api) && incomingBattleDamage(api) > 0,
    canActivate: (api) => api.resolving() || (inDamage(api) && incomingBattleDamage(api) > 0),
    execute: (api) => {
      api.zeroThisAttackDamage();
    },
  });

  // Metal Raiders, 26 June 2002: "Reduce the ATK of an opponent's monster
  // attacking this card to 0." Same operation as c25955164.lua,
  // c62340868.lua, and c98434877.lua: opponent's turn, this card is the
  // attack target, the attacker's ATK becomes 0 for this damage calculation,
  // once while this copy is face-up.
  const guardianAnswers = (api: EngineApi): boolean => {
    if (api.source.faceUpEffectUsed) return false;
    if (api.state.turnPlayer === api.controller) return false;
    if (!inDamage(api)) return false;
    return api.state.pendingAttack?.targetUid === api.source.uid;
  };
  for (const id of GUARDIANS) {
    engineEffects.register({
      id,
      trigger: EngineEffectTrigger.ACTIVATE,
      damageStep: true,
      relevantNow: guardianAnswers,
      canActivate: (api) => api.resolving() || guardianAnswers(api),
      execute: (api) => {
        const attackerUid = api.state.pendingAttack?.attackerUid;
        if (!attackerUid) return;
        api.emit({
          type: EventType.MESSAGE,
          text: "The attacking monster's ATK becomes 0.",
          code: 'guardianAtkZero',
          vars: { cardId: api.source.cardId },
        });
        api.setAtkFinal(attackerUid, 0);
      },
    });
  }

  // Elegant Egotist (90219263). A face-up Harpie Lady on either side of
  // the field, matching c90219263.lua and the album text. The summon is
  // chosen when the Spell resolves, in GameEngine, so the Deck card stays
  // hidden until then.
  engineEffects.register({
    id: ELEGANT_EGOTIST,
    trigger: EngineEffectTrigger.ACTIVATE,
    canActivate: (api) => egotistReady(api),
    execute: () => {
      /* GameEngine.resolveElegantEgotist */
    },
  });
}

function handDragons(api: EngineApi): CardInstance[] {
  return api.state.zones[api.controller].hand.filter((c) => {
    const data = api.library.get(c.cardId);
    return !!data && getCardKind(data) === CardKind.MONSTER && data.race === 'Dragon';
  });
}
